import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Flame, Calendar, Star } from 'lucide-react';
import CyclingTitle from './primitives/CyclingTitle';
import EmptyState from './primitives/EmptyState';
import { Sparkline } from './primitives/Sparkline';
import { useProgressiveOverload } from '../hooks/useProgressiveOverload';
import { useProactiveInsight } from '../hooks/useProactiveInsight';
import {
  computeTrainStats, weeklyMileageHistory, monthlyMileageHistory,
  yearlyMileageHistory, topWeightForExercise,
} from '../utils/trainStats';
import { createWorkoutEvent } from '../utils/calendarSync';

// ─── Date helpers ──────────────────────────────────────────────────────────────
const TODAY = new Date().toISOString().slice(0, 10);
const addDays = (iso, n) => {
  const d = new Date(iso + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const fmtDate  = (iso) => new Date(iso+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'});
const fmtMonth = (ym)  => { const d=new Date(ym+'-01T12:00:00'); return d.toLocaleDateString('en-US',{month:'short',year:'numeric'}); };
const daysUntil = (iso) => Math.round((new Date(iso+'T12:00:00')-new Date(TODAY+'T12:00:00'))/86400000);
const isoWeekStart = (iso) => {
  const d=new Date(iso+'T12:00:00'); const dow=(d.getDay()+6)%7;
  const m=new Date(d); m.setDate(d.getDate()-dow); return m.toISOString().slice(0,10);
};

// ─── Constants ────────────────────────────────────────────────────────────────
const CARDIO_TYPES   = ['Long Run','Easy Run','Tempo Run','Interval','Cross Train','Bike','Swim','Walk'];
const STRENGTH_TYPES = ['Gym','Strength','Upper Body','Lower Body','Full Body'];
const COMMON_EXERCISES = [
  'Bench Press','Squat','Deadlift','Overhead Press','Pull-ups','Rows',
  'Romanian Deadlift','Leg Press','Hip Thrust','Incline Bench',
  'Dumbbell Curl','Tricep Pushdown','Lat Pulldown','Cable Row',
  'Lunges','Bulgarian Split Squat','Hack Squat','Leg Curl',
];
const isStrength = (t) => ['gym','strength','upper body','lower body','full body'].includes((t||'').toLowerCase());
const isCardio   = (t) => CARDIO_TYPES.map(x=>x.toLowerCase()).includes((t||'').toLowerCase());
const RUN_KW = ['run','workout','gym','train','exercise','strength'];

// ─── Avatars ──────────────────────────────────────────────────────────────────
function MyloIcon({ size = 28 }) {
  const r = Math.round(size*0.25);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{display:'block',flexShrink:0}}>
      <rect width={size} height={size} rx={r} fill="var(--s2)" stroke="var(--b1)" strokeWidth="0.5"/>
      <text x={size/2} y={size*0.72} textAnchor="middle" fontFamily="Georgia,serif"
        fontSize={Math.round(size*0.55)} fontWeight="600" fill="var(--ink)">m</text>
      <circle cx={size*0.67} cy={size*0.29} r={Math.max(2,size*0.09)} fill="var(--accent)"/>
    </svg>
  );
}
function UserAvatar({ size=28, initial='K' }) {
  return <div style={{width:size,height:size,borderRadius:'50%',background:'var(--s3)',
    display:'flex',alignItems:'center',justifyContent:'center',
    fontSize:11,fontFamily:'var(--sans)',color:'var(--t2)',flexShrink:0,fontWeight:500}}>{initial}</div>;
}

// ─── Plan parsing ─────────────────────────────────────────────────────────────
const parsePlan   = (t) => { const m=t.match(/<<<PLAN>>>([\s\S]*?)<<<\/PLAN>>>/); if(!m)return null; try{return JSON.parse(m[1].trim());}catch{return null;} };
const stripPlan   = (t) => t.replace(/<<<PLAN>>>[\s\S]*?<<<\/PLAN>>>/g,'').trim();

// ─── Exercise builder (shared between log form and routine builder) ────────────
function ExerciseBuilder({ exercises, onChange, progressiveData, knownExercises, routineWeights = {} }) {
  const addEx    = () => onChange([...exercises, {name:'',sets:[{reps:'',weight:''}]}]);
  const removeEx = (i) => onChange(exercises.filter((_,x)=>x!==i));
  const updateExName = (i,v) => { const e=[...exercises]; e[i]={...e[i],name:v}; onChange(e); };
  const addSet   = (i) => { const e=[...exercises]; e[i]={...e[i],sets:[...e[i].sets,{reps:'',weight:''}]}; onChange(e); };
  const removeSet= (ei,si) => { const e=[...exercises]; e[ei]={...e[ei],sets:e[ei].sets.filter((_,x)=>x!==si)}; onChange(e); };
  const updateSet= (ei,si,f,v) => { const e=[...exercises]; const s=[...e[ei].sets]; s[si]={...s[si],[f]:v}; e[ei]={...e[ei],sets:s}; onChange(e); };
  const repeat3  = (ei) => {
    const e=[...exercises]; const last=e[ei].sets.slice(-1)[0]||{reps:'',weight:''};
    while(e[ei].sets.length<3) e[ei]={...e[ei],sets:[...e[ei].sets,{...last}]};
    onChange(e);
  };
  return (
    <div className="tr-exlog">
      {exercises.map((ex,ei)=>{
        const prog=progressiveData?.[ex.name]; const sugg=prog?.suggestion; const last=prog?.lastSession;
        const rw=routineWeights[ex.name];
        return (
          <div key={ei} className="tr-ex-row">
            <div className="tr-ex-name-row">
              <span className="tr-drag-handle">⠿</span>
              <input className="tr-input" style={{flex:1,height:36,border:'0.5px solid var(--s3)',background:'var(--s1)',fontSize:13}}
                placeholder="exercise name" list={`ex-${ei}`}
                value={ex.name} onChange={e=>updateExName(ei,e.target.value)}/>
              <datalist id={`ex-${ei}`}>{knownExercises.map(n=><option key={n} value={n}/>)}</datalist>
              <button className="tr-rm-btn" onClick={()=>removeEx(ei)}>×</button>
            </div>
            {ex.name && last && <div className="tr-ex-hint">last: {last.sets.map(s=>`${s.reps}×${s.weight}lb`).join(', ')}{sugg&&` · suggested: ${sugg.sets}×${sugg.reps}×${sugg.weight}lb`}</div>}
            {ex.name && rw && rw>0 && <div className="tr-ex-routine-hint">routine: {rw}lb</div>}
            {ex.sets.map((set,si)=>(
              <div key={si} className="tr-set-row" style={{gap:6}}>
                <span className="tr-set-lbl">set {si+1}</span>
                <input type="number" className="tr-set-input" style={{width:44}} placeholder={sugg?String(sugg.reps):'reps'}
                  value={set.reps} onChange={e=>updateSet(ei,si,'reps',e.target.value)}/>
                <span className="tr-set-lbl">×</span>
                <input type="number" className="tr-set-input" style={{width:44}} placeholder={sugg?String(sugg.reps):'reps'}
                  value={set.reps} onChange={e=>updateSet(ei,si,'reps',e.target.value)}/>
                <span className="tr-set-lbl">×</span>
                <input type="number" className="tr-set-input" style={{width:56}} placeholder={sugg?String(sugg.weight):'lb'}
                  value={set.weight} onChange={e=>updateSet(ei,si,'weight',e.target.value)}/>
                <span className="tr-set-lbl">lb</span>
                {ex.sets.length>1&&<button className="tr-rm-btn" onClick={()=>removeSet(ei,si)}>×</button>}
              </div>
            ))}
            <div style={{display:'flex',gap:'var(--sp2)'}}>
              <button className="tr-add-set" onClick={()=>addSet(ei)}>+ set</button>
              <button className="tr-repeat3" onClick={()=>repeat3(ei)}>repeat ×3</button>
            </div>
          </div>
        );
      })}
      <button className="tr-add-ex" onClick={addEx}>+ add exercise</button>
    </div>
  );
}

// ─── Routine builder bottom sheet ─────────────────────────────────────────────
// ─── Unit toggle with animated sliding thumb ─────────────────────────────────
function UnitToggle({ unit, onChange }) {
  const miRef = useRef(null);
  const kmRef = useRef(null);
  const thumbRef = useRef(null);
  useEffect(()=>{
    const active = unit==='mi' ? miRef.current : kmRef.current;
    if(active&&thumbRef.current){
      thumbRef.current.style.width = active.offsetWidth+'px';
      thumbRef.current.style.transform = unit==='mi' ? 'translateX(0)' : `translateX(${miRef.current?.offsetWidth||0}px)`;
    }
  },[unit]);
  return(
    <div style={{display:'flex',background:'var(--s2)',borderRadius:9,padding:3,position:'relative',flexShrink:0}}>
      <div ref={thumbRef} style={{position:'absolute',top:3,left:3,height:'calc(100% - 6px)',background:'var(--s1)',borderRadius:7,boxShadow:'0 1px 2px rgba(0,0,0,0.08)',transition:'transform 0.18s ease, width 0.18s ease',pointerEvents:'none',zIndex:0}}/>
      {['mi','km'].map(u=>(
        <button key={u} ref={u==='mi'?miRef:kmRef}
          style={{padding:'6px 12px',borderRadius:7,border:'none',fontFamily:'var(--sans)',fontSize:11,letterSpacing:'0.2px',cursor:'pointer',position:'relative',zIndex:1,
            background:'transparent',color:unit===u?'var(--ink)':'var(--t3)',fontWeight:unit===u?500:400,transition:'color 0.15s'}}
          onClick={()=>onChange(u)}>min / {u}</button>
      ))}
    </div>
  );
}

// ─── Exercise builder V2 (last-weight col + completion circles) ───────────────
function ExerciseBuilderV2({ exercises, onChange, progressiveData, knownExercises, sortedWorkouts, routineWeights={}, hideCompletion=false }) {
  function getLastWeight(exName, setIdx) {
    if(!exName) return null;
    const prev = (sortedWorkouts||[]).find(w=>w.exercises?.some(e=>e.name===exName));
    if(!prev) return null;
    const ex = prev.exercises.find(e=>e.name===exName);
    return ex?.sets?.[setIdx]?.weight || ex?.sets?.[0]?.weight || null;
  }
  const addEx    = () => onChange([...exercises,{name:'',sets:[{reps:'',weight:'',done:false}]}]);
  const removeEx = (i) => onChange(exercises.filter((_,x)=>x!==i));
  const updateExName = (i,v) => { const e=[...exercises]; e[i]={...e[i],name:v}; onChange(e); };
  const addSet   = (i) => { const e=[...exercises]; e[i]={...e[i],sets:[...e[i].sets,{reps:'',weight:'',done:false}]}; onChange(e); };
  const updateSet= (ei,si,f,v) => { const e=[...exercises]; const s=[...e[ei].sets]; s[si]={...s[si],[f]:v}; e[ei]={...e[ei],sets:s}; onChange(e); };
  const toggleDone=(ei,si) => updateSet(ei,si,'done',!exercises[ei].sets[si].done);
  const repeat3  = (ei) => {
    const first = exercises[ei].sets[0];
    if(!first?.reps&&!first?.weight) return;
    onChange(exercises.map((ex,i)=>i===ei?{...ex,sets:[{...first},{...first},{...first}]}:ex));
  };
  return(
    <div style={{display:'flex',flexDirection:'column'}}>
      {exercises.map((ex,ei)=>{
        const prog=progressiveData?.[ex.name]; const sugg=prog?.suggestion; const lastSess=prog?.lastSession;
        return(
          <div key={ei} style={{background:'var(--s2)',borderRadius:10,padding:'12px 14px',marginBottom:8}}>
            <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:8}}>
              <input style={{flex:1,height:32,background:'var(--s1)',border:'0.5px solid var(--s3)',borderRadius:7,padding:'0 8px',fontFamily:'var(--sans)',fontSize:13,color:'var(--ink)',outline:'none'}}
                placeholder="exercise name" list={`exv2-${ei}`}
                value={ex.name} onChange={e=>updateExName(ei,e.target.value)}/>
              <datalist id={`exv2-${ei}`}>{(knownExercises||[]).map(n=><option key={n} value={n}/>)}</datalist>
              <button style={{background:'transparent',border:'none',color:'var(--t4)',cursor:'pointer',fontSize:14,minWidth:32,minHeight:32,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>removeEx(ei)}>×</button>
            </div>
            {/* sets × reps × weight — single template row */}
            <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap',marginBottom:6}}>
              <span style={{fontSize:11,color:'var(--t3)',fontFamily:'var(--sans)'}}>sets</span>
              <input type="number" min="1" max="10"
                value={ex.sets.length||1}
                onChange={e=>{const n=Math.max(1,Math.min(10,parseInt(e.target.value)||1));const cur=ex.sets[0]||{reps:'',weight:''};onChange(exercises.map((x,i)=>i===ei?{...x,sets:Array.from({length:n},()=>({...cur,done:false}))}:x));}}
                style={{width:44,height:32,border:'0.5px solid var(--s3)',borderRadius:7,textAlign:'center',fontSize:14,fontFamily:'var(--sans)',background:'var(--s1)',color:'var(--ink)',outline:'none'}}/>
              <span style={{fontSize:11,color:'var(--t3)',fontFamily:'var(--sans)'}}>×</span>
              <input type="number" placeholder="reps"
                value={ex.sets[0]?.reps||''}
                onChange={e=>onChange(exercises.map((x,i)=>i===ei?{...x,sets:x.sets.map(s=>({...s,reps:e.target.value}))}:x))}
                style={{width:52,height:32,border:'0.5px solid var(--s3)',borderRadius:7,textAlign:'center',fontSize:14,fontFamily:'var(--sans)',background:'var(--s1)',color:'var(--ink)',outline:'none'}}/>
              <span style={{fontSize:11,color:'var(--t3)',fontFamily:'var(--sans)'}}>×</span>
              <input type="number" placeholder="lb"
                value={ex.sets[0]?.weight||''}
                onChange={e=>onChange(exercises.map((x,i)=>i===ei?{...x,sets:x.sets.map(s=>({...s,weight:e.target.value}))}:x))}
                style={{width:58,height:32,border:'0.5px solid var(--s3)',borderRadius:7,textAlign:'center',fontSize:14,fontFamily:'var(--sans)',background:'var(--s1)',color:'var(--ink)',outline:'none'}}/>
              <span style={{fontSize:11,color:'var(--t3)',fontFamily:'var(--sans)'}}>lb</span>
            </div>
            {ex.name&&(sugg||lastSess)&&(
              <div style={{fontFamily:'var(--sans)',fontSize:10,color:'var(--t4)',marginTop:6}}>
                {sugg&&<>suggested next: <span style={{color:'var(--accent)'}}>{sugg.weight}lb</span></>}
                {sugg&&lastSess&&' · '}
                {lastSess&&<>last session: {lastSess.sets.map(s=>`${s.reps}×${s.weight}lb`).join(', ')}</>}
              </div>
            )}
          </div>
        );
      })}
      <button style={{fontFamily:'var(--sans)',fontSize:13,color:'var(--accent)',background:'transparent',border:'none',cursor:'pointer',padding:'4px 0',alignSelf:'flex-start'}} onClick={addEx}>+ add exercise</button>
    </div>
  );
}

// ─── Inline routine builder (replaces bottom sheet) ──────────────────────────
function RoutineInline({ initial, editId, onSave, onDelete, onClose, knownExercises, progressiveData, sortedWorkouts=[] }) {
  const [name,  setName]  = useState(initial?.name || '');
  const [type,  setType]  = useState(initial?.type || 'Strength');
  const [exs,   setExs]   = useState(
    initial?.exercises?.map(e=>({name:e.name||'',sets:Array.from({length:e.sets||1},()=>({reps:String(e.reps||''),weight:String(e.weight||'')})),})) || [{name:'',sets:[{reps:'',weight:''}]}]
  );
  const [nlInput,   setNlInput]   = useState('');
  const [nlParsing, setNlParsing] = useState(false);
  const [nlConfirm, setNlConfirm] = useState(null);
  const [showForm,  setShowForm]  = useState(!!editId);
  const DEFAULT_TYPE_PILLS = ['Strength','Gym','Long Run','Easy Run','Cross Train'];
  const TYPE_PILLS = [...new Set([...DEFAULT_TYPE_PILLS, ...(customWorkoutTypes||[])])];
  const formRef = useRef(null);

  useEffect(()=>{ formRef.current?.scrollIntoView({behavior:'smooth',block:'nearest'}); },[]);

  async function parseRoutineNL() {
    const text = nlInput.trim();
    if (!text || nlParsing) return;
    setNlParsing(true);
    try {
      const prompt = `Parse this workout routine description into structured JSON.\nReturn ONLY valid JSON, no other text.\n\nInput: "${text}"\n\nReturn:\n{\n  "name": "string",\n  "type": "Strength"|"Gym"|"Long Run"|"Easy Run"|"Tempo Run"|"Cross Train",\n  "exercises": [\n    { "name": "string", "sets": number, "reps": number, "weight": number }\n  ]\n}\n\nExamples:\n"upper body: bench 3x8x135, overhead press 3x8x95" → {"name":"Upper Body","type":"Strength","exercises":[{"name":"Bench Press","sets":3,"reps":8,"weight":135},{"name":"Overhead Press","sets":3,"reps":8,"weight":95}]}\n"leg day: squat 5x5x185, rdl 3x10x135" → {"name":"Leg Day","type":"Strength","exercises":[{"name":"Squat","sets":5,"reps":5,"weight":185},{"name":"Romanian Deadlift","sets":3,"reps":10,"weight":135}]}`;
      const res  = await fetch('/api/chat',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({model:'claude-sonnet-4-20250514',max_tokens:400,messages:[{role:'user',content:prompt}]})});
      const data = await res.json();
      const raw  = data.content?.[0]?.text||'';
      const parsed = JSON.parse(raw.replace(/```json?/g,'').replace(/```/g,'').trim());
      setNlConfirm(parsed);
      setNlInput('');
    } catch { setShowForm(true); }
    setNlParsing(false);
  }

  function applyConfirm() {
    if (!nlConfirm) return;
    setName(nlConfirm.name||'');
    setType(nlConfirm.type||'Strength');
    setExs((nlConfirm.exercises||[]).map(e=>({name:e.name,sets:Array.from({length:e.sets||1},()=>({reps:String(e.reps||''),weight:String(e.weight||'')}))})));
    setNlConfirm(null);
    setShowForm(true);
  }

  function saveConfirm() {
    if (!nlConfirm) return;
    const routine = {
      id: editId||`r_${Date.now()}`,
      name: nlConfirm.name||'New Routine',
      type: nlConfirm.type||'Strength',
      exercises: (nlConfirm.exercises||[]).map(e=>({name:e.name,sets:parseInt(e.sets)||3,reps:parseInt(e.reps)||8,weight:parseFloat(e.weight)||0})),
      lastUsed: initial?.lastUsed||null, useCount: initial?.useCount||0,
    };
    onSave(routine);
  }

  function save() {
    if (!name.trim()) return;
    const routine = {
      id: editId||`r_${Date.now()}`, name:name.trim(), type,
      exercises: exs.filter(e=>e.name.trim()).map(e=>({
        name:e.name.trim(),
        sets:e.sets.filter(s=>s.reps||s.weight).length||3,
        reps:parseInt(e.sets[0]?.reps)||8,
        weight:parseFloat(e.sets[0]?.weight)||0,
      })),
      lastUsed:initial?.lastUsed||null, useCount:initial?.useCount||0,
    };
    onSave(routine);
  }

  return (
    <div className="tr-inline-form" ref={formRef}>
      {/* Header row */}
      <div className="tr-inline-hdr">
        <span className="tr-inline-hdr-label">{editId ? (name||'edit routine') : 'new routine'}</span>
        <button className="tr-inline-cancel" onClick={onClose}>× cancel</button>
      </div>

      {/* NL input — new routine only */}
      {!editId && !nlConfirm && !showForm && (
        <>
          <div className="tr-nl-bar">
            <textarea className="tr-nl-ta" rows={1} placeholder="describe your routine…"
              value={nlInput}
              onChange={e=>{setNlInput(e.target.value);e.target.style.height='auto';e.target.style.height=Math.min(e.target.scrollHeight,80)+'px';}}
              onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();parseRoutineNL();}}}/>
            <button className={`tr-nl-send ${nlInput.trim()?'tr-nl-send-on':''}`} onClick={parseRoutineNL} disabled={!nlInput.trim()||nlParsing}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 10V2M2 6l4-4 4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>
          {nlParsing&&<div className="tr-parsing"><div className="tr-dot"/><div className="tr-dot"/><div className="tr-dot"/><span style={{fontFamily:'var(--sans)',fontSize:'var(--fs-xs)',color:'var(--t3)',marginLeft:4}}>building routine…</span></div>}
          <button className="tr-add-ex" style={{marginTop:4}} onClick={()=>setShowForm(true)}>fill out manually instead</button>
        </>
      )}

      {/* NL confirmation card */}
      {nlConfirm && !showForm && (
        <div className="tr-confirm-card">
          <div className="tr-confirm-text">{(nlConfirm.name||'').toLowerCase()} · {nlConfirm.exercises?.length||0} exercises</div>
          <button className="tr-confirm-edit" onClick={applyConfirm}>edit</button>
          <button className="tr-confirm-log" onClick={saveConfirm}>save routine →</button>
        </div>
      )}

      {/* Structured form */}
      {(showForm || editId) && (
        <>
          {!editId && <button className="tr-add-ex" style={{marginBottom:4,fontSize:11,color:'var(--t3)'}} onClick={()=>{setShowForm(false);setNlConfirm(null);}}>← back to quick entry</button>}
          {editId && <button className="tr-add-ex" style={{marginBottom:4,fontSize:11,color:'var(--t3)'}} onClick={()=>setShowForm(false)}>re-parse ↺</button>}
          <div className="tr-field"><label className="tr-field-lbl">name</label>
            <input className="tr-input" style={{height:36,border:'0.5px solid var(--s3)',background:'var(--s1)',fontSize:13}}
              placeholder="e.g. upper body a" value={name} onChange={e=>setName(e.target.value)}/></div>
          <div className="tr-field"><label className="tr-field-lbl">type</label>
            <div className="tr-pill-row">{TYPE_PILLS.map(t=><button key={t} className={`tr-pill ${type===t?'tr-pill-active':''}`} onClick={()=>setType(t)}>{t.toLowerCase()}</button>)}</div></div>
          <div className="tr-field"><label className="tr-field-lbl">exercises</label>
            <ExerciseBuilderV2 exercises={exs} onChange={setExs} progressiveData={progressiveData} knownExercises={knownExercises} sortedWorkouts={sortedWorkouts} hideCompletion={true}/></div>
        </>
      )}

      {/* Footer actions */}
      <button className="tr-submit"
        style={{width:'100%',height:40,marginTop:8}}
        onClick={showForm||editId ? save : (nlConfirm ? saveConfirm : ()=>{})}
        disabled={showForm||editId ? !name.trim() : !nlConfirm}>
        {editId?'save changes':'save routine'}
      </button>
      {editId&&onDelete&&(
        <button className="tr-delete-link" onClick={()=>onDelete(editId)}>delete routine</button>
      )}
    </div>
  );
}

// ─── Highlights carousel ──────────────────────────────────────────────────────
function HighlightsCarousel({ workouts, triumphs, stats }) {
  const [slide, setSlide] = useState(0);
  const intervalRef = useRef(null);
  const fmtShort = (iso) => new Date(iso+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'});
  const fmtMonthShort = (ym) => new Date(ym+'-01T12:00:00').toLocaleDateString('en-US',{month:'short',year:'numeric'}).toLowerCase();
  const allSlides = useMemo(()=>{
    const s=[];
    if(stats.lifetimeMiles>0)s.push({value:stats.lifetimeMiles.toFixed(1),context:'miles logged lifetime',accent:`≈ ${Math.floor(stats.lifetimeMiles/26.2)} marathons`});
    if(stats.longestRun>0)s.push({value:`${stats.longestRun} mi`,context:'longest run ever',accent:stats.longestRunDate?fmtShort(stats.longestRunDate):null});
    if(stats.currentStreak>0)s.push({value:stats.currentStreak,context:'day run streak',accent:stats.currentStreak===stats.longestStreak?'on a roll':`personal best: ${stats.longestStreak} days`});
    if(stats.liftingStreak>0)s.push({value:stats.liftingStreak,context:'consecutive lifting days',accent:`best: ${stats.bestLiftingStreak} days`});
    if(stats.totalWorkouts>0)s.push({value:stats.totalWorkouts,context:'workouts logged',accent:stats.firstWorkout?`since ${fmtMonthShort(stats.firstWorkout.slice(0,7))}`:null});
    if(stats.favoriteType)s.push({value:stats.favoriteType.toLowerCase(),context:'most logged activity',accent:`${stats.favoriteTypeCount} sessions`});
    if(stats.bestMonth)s.push({value:`${stats.bestMonth.miles} mi`,context:'best month ever',accent:fmtMonthShort(stats.bestMonth.key)});
    const latestRace=[...(triumphs||[])].filter(t=>t.type==='race').sort((a,b)=>b.date.localeCompare(a.date))[0];
    if(latestRace)s.push({value:latestRace.displayValue,context:`${latestRace.title?.toLowerCase()||''} · ${latestRace.category}`,accent:fmtShort(latestRace.date)});
    const milePR=(triumphs||[]).find(t=>t.type==='pr'&&t.category==='mile');
    if(milePR)s.push({value:milePR.displayValue,context:'fastest mile',accent:fmtShort(milePR.date)});
    const marathonPR=(triumphs||[]).find(t=>t.type==='pr'&&t.category==='marathon');
    if(marathonPR)s.push({value:marathonPR.displayValue,context:'marathon pr',accent:fmtShort(marathonPR.date)});
    const raceCount=(triumphs||[]).filter(t=>t.type==='race').length;
    if(raceCount>0){const fr=[...(triumphs||[])].filter(t=>t.type==='race').sort((a,b)=>a.date.localeCompare(b.date))[0];s.push({value:raceCount,context:'races completed',accent:`first: ${fr?.title?.toLowerCase()||''}`});}
    const lm=[...(triumphs||[])].filter(t=>t.type==='milestone').sort((a,b)=>b.date.localeCompare(a.date))[0];
    if(lm)s.push({value:lm.title?.toLowerCase(),context:'milestone reached',accent:fmtShort(lm.date)});
    return s;
  },[stats,triumphs]);
  const startInterval=useCallback(()=>{if(intervalRef.current)clearInterval(intervalRef.current);if(allSlides.length>1)intervalRef.current=setInterval(()=>setSlide(i=>(i+1)%allSlides.length),3500);},[allSlides.length]);
  useEffect(()=>{setSlide(0);startInterval();return()=>clearInterval(intervalRef.current);},[allSlides.length]);
  function advance(){setSlide(i=>(i+1)%allSlides.length);startInterval();}
  if(allSlides.length===0) return (
    <div className="tr-carousel" style={{cursor:'default'}}>
      <EmptyState message="no highlights yet" cta={{label:'+ log your first workout', onClick:()=>{}}}/>
    </div>
  );
  const sl=allSlides[slide]||allSlides[0];
  return(
    <div className="tr-carousel" onClick={advance}>
      <div className="tr-carousel-slide" key={slide}>
        <div className="tr-carousel-val">{sl.value}</div>
        <div className="tr-carousel-ctx">{sl.context}</div>
        {sl.accent&&<div className="tr-carousel-accent">{sl.accent}</div>}
      </div>
      {allSlides.length>1&&<div className="tr-carousel-dots">{allSlides.map((_,i)=><div key={i} className={`tr-carousel-dot ${i===slide?'tr-carousel-dot-active':''}`} onClick={e=>{e.stopPropagation();setSlide(i);startInterval();}}/>)}</div>}
    </div>
  );
}

// ─── Triumph inline form ──────────────────────────────────────────────────────
function TriumphInline({ mode, initial, onSave, onClose }) {
  const isRun=(cat)=>['marathon','half','5k','10k','mile'].includes(cat);
  const [cat,  setCat]  = useState(initial?.category||'marathon');
  const [val,  setVal]  = useState(initial?.displayValue||'');
  const [name, setName] = useState(initial?.title||'');
  const [date, setDate] = useState(initial?.date||new Date().toISOString().slice(0,10));
  const [note, setNote] = useState(initial?.note||'');
  const [isPR, setIsPR] = useState(initial?.pr||false);
  const ref=useRef(null);
  useEffect(()=>{ref.current?.scrollIntoView({behavior:'smooth',block:'nearest'});},[]);
  const PR_CATS=['marathon','half','5k','10k','mile','bench','squat','deadlift','custom'];
  const RACE_TYPES=['marathon','half','10k','5k','other'];
  function save(){onSave({id:initial?.id||`t_${Date.now()}`,type:mode==='race'?'race':mode==='milestone'?'milestone':'pr',category:mode==='race'?cat:mode==='milestone'?'custom':cat,title:mode==='race'?name:mode==='milestone'?name:cat,value:0,unit:mode==='race'||isRun(cat)?'min':'lb',date,note,displayValue:val,pr:isPR||mode==='pr'});}
  return(
    <div className="tr-inline-form" ref={ref}>
      <div className="tr-inline-hdr"><span className="tr-inline-hdr-label">{initial?`edit ${mode}`:`new ${mode}`}</span><button className="tr-inline-cancel" onClick={onClose}>× cancel</button></div>
      {mode==='pr'&&<><div className="tr-field"><label className="tr-field-lbl">category</label><div className="tr-pill-row">{PR_CATS.map(c=><button key={c} className={`tr-pill ${cat===c?'tr-pill-active':''}`} onClick={()=>setCat(c)}>{c}</button>)}</div></div><div className="tr-field"><label className="tr-field-lbl">{isRun(cat)?'time (hh:mm:ss)':'weight (lb)'}</label><input className="tr-input" style={{height:36,border:'0.5px solid var(--s3)',background:'var(--s1)',fontSize:13}} placeholder={isRun(cat)?'e.g. 4:02:14':'e.g. 185'} value={val} onChange={e=>setVal(e.target.value)}/></div></>}
      {mode==='race'&&<><div className="tr-field"><label className="tr-field-lbl">race name</label><input className="tr-input" style={{height:36,border:'0.5px solid var(--s3)',background:'var(--s1)',fontSize:13}} placeholder="e.g. Brooklyn Marathon" value={name} onChange={e=>setName(e.target.value)}/></div><div className="tr-field"><label className="tr-field-lbl">type</label><div className="tr-pill-row">{RACE_TYPES.map(c=><button key={c} className={`tr-pill ${cat===c?'tr-pill-active':''}`} onClick={()=>setCat(c)}>{c}</button>)}</div></div><div className="tr-field"><label className="tr-field-lbl">finish time</label><input className="tr-input" style={{height:36,border:'0.5px solid var(--s3)',background:'var(--s1)',fontSize:13}} placeholder="hh:mm:ss" value={val} onChange={e=>setVal(e.target.value)}/></div><div className="tr-field" style={{flexDirection:'row',alignItems:'center',gap:8}}><input type="checkbox" checked={isPR} onChange={e=>setIsPR(e.target.checked)} id="pr-chk" style={{accentColor:'var(--accent)'}}/><label htmlFor="pr-chk" className="tr-field-lbl" style={{marginBottom:0}}>this was a PR</label></div></>}
      {mode==='milestone'&&<div className="tr-field"><label className="tr-field-lbl">milestone</label><input className="tr-input" style={{height:36,border:'0.5px solid var(--s3)',background:'var(--s1)',fontSize:13}} placeholder="e.g. first trail run" value={name} onChange={e=>setName(e.target.value)}/></div>}
      <div className="tr-form-row"><div className="tr-field"><label className="tr-field-lbl">date</label><input type="date" className="tr-input" value={date} onChange={e=>setDate(e.target.value)}/></div><div className="tr-field"><label className="tr-field-lbl">note</label><input className="tr-input" style={{height:36}} placeholder="optional" value={note} onChange={e=>setNote(e.target.value)}/></div></div>
      <button className="tr-submit" style={{width:'100%',height:40,marginTop:8}} onClick={save} disabled={mode==='milestone'?!name.trim():mode==='race'?!name.trim()&&!val:!val}>{initial?'save changes':mode==='race'?'log race':mode==='milestone'?'add milestone':'save pr'}</button>
    </div>
  );
}

// ─── Proactive insight card ───────────────────────────────────────────────────
function InsightCard({ insight, onCta, onDismiss }) {
  if (!insight) return null;
  return (
    <div className="tr-insight-card">
      <button className="tr-insight-dismiss" onClick={onDismiss}>×</button>
      <div className="tr-insight-hl">{insight.headline}</div>
      <div className="tr-insight-body">{insight.body}</div>
      {insight.cta&&<button className="tr-insight-cta" onClick={()=>onCta(insight)}>{insight.cta}</button>}
    </div>
  );
}

// ─── CSS ──────────────────────────────────────────────────────────────────────
const CSS_TRAIN = `
.tr-page{max-width:760px;margin:0 auto;padding:var(--sp4) var(--sp4) var(--sp8);font-family:var(--sans);display:flex;flex-direction:column;overflow-x:hidden;}
@media(max-width:768px){.tr-page{padding-bottom:calc(var(--bot-nav-h,56px)+var(--sp10)+var(--safe-bottom,0px));}}
.tr-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:var(--sp3);}
.tr-log-btn{height:30px;padding:0 14px;background:var(--accent);border:none;border-radius:var(--r2);color:var(--on-accent);font-family:var(--sans);font-size:var(--fs-xs);font-weight:500;cursor:pointer;transition:opacity .12s;flex-shrink:0;-webkit-tap-highlight-color:transparent;}
.tr-log-btn:hover{opacity:.88;}
.tr-view{width:100%;}
.tr-lbl{font-family:var(--sans);font-size:11px;font-weight:400;color:var(--t3);letter-spacing:0.05em;text-transform:lowercase;margin-bottom:8px;}
/* stat pair */
.tr-stats{display:flex;gap:var(--sp3);margin-bottom:var(--sp4);}
.tr-stat{background:var(--s1);border:0.5px solid var(--s2);border-radius:var(--r3);padding:10px 14px;flex:1;display:flex;flex-direction:column;gap:2px;}
.tr-stat-val{font-family:var(--sans);font-size:20px;font-weight:600;color:var(--ink);line-height:1;}
.tr-stat-lbl{font-family:var(--sans);font-size:10px;color:var(--t3);letter-spacing:0.04em;}
/* insight card */
.tr-insight-card{position:relative;background:var(--s1);border-left:2px solid var(--accent);border-radius:0 12px 12px 0;padding:14px 36px 14px 16px;margin-bottom:var(--sp4);}
.tr-insight-dismiss{position:absolute;top:10px;right:10px;background:transparent;border:none;color:var(--t4);font-size:16px;cursor:pointer;padding:2px 4px;line-height:1;}
.tr-insight-hl{font-family:var(--sans);font-size:13px;color:var(--ink);font-weight:400;margin-bottom:4px;}
.tr-insight-body{font-family:var(--sans);font-size:12px;color:var(--t2);margin-bottom:6px;}
.tr-insight-cta{background:transparent;border:none;font-family:var(--sans);font-size:12px;color:var(--accent);cursor:pointer;padding:0;}
/* intel (pre-session) */
.tr-intel{background:var(--s1);border-left:2px solid var(--accent);border-radius:0 12px 12px 0;padding:14px 16px;margin-bottom:var(--sp4);}
.tr-intel-hdr{font-family:var(--sans);font-size:11px;color:var(--t3);letter-spacing:0.04em;margin-bottom:4px;}
.tr-intel-title{font-family:var(--sans);font-size:14px;font-weight:500;color:var(--ink);margin-bottom:6px;}
.tr-intel-row{font-family:var(--sans);font-size:13px;color:var(--t2);margin-bottom:3px;}
.tr-intel-accent{color:var(--accent);}
.tr-intel-sub{font-family:var(--sans);font-size:11px;color:var(--t4);margin-top:6px;}
.tr-intel-action{background:transparent;border:none;font-family:var(--sans);font-size:13px;color:var(--accent);cursor:pointer;padding:0;margin-top:6px;}
/* NL input */
.tr-nl-bar{display:flex;gap:var(--sp2);align-items:flex-end;background:var(--s1);border:0.5px solid var(--b2);border-radius:var(--r3);padding:var(--sp2) var(--sp3);margin-bottom:var(--sp2);transition:border-color .12s;}
.tr-nl-bar:focus-within{border-color:var(--accent);box-shadow:0 0 0 2px var(--accent-w);}
.tr-nl-ta{flex:1;background:transparent;border:none;outline:none;font-family:var(--sans);font-size:var(--fs-sm);color:var(--ink);resize:none;min-height:22px;max-height:100px;line-height:1.5;}
.tr-nl-ta::placeholder{color:var(--t4);}
.tr-nl-send{width:28px;height:28px;border-radius:50%;border:none;display:flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;transition:background .1s;background:var(--b2);color:var(--t3);}
.tr-nl-send-on{background:var(--accent)!important;color:var(--on-accent)!important;}
.tr-parsing{font-family:var(--sans);font-size:var(--fs-xs);color:var(--t3);padding:var(--sp1) 0;display:flex;align-items:center;gap:4px;}
@keyframes tr-pulse{0%,100%{opacity:.3}50%{opacity:1}}
.tr-dot{width:4px;height:4px;border-radius:50%;background:var(--t3);animation:tr-pulse 1s ease-in-out infinite;}
.tr-dot:nth-child(2){animation-delay:.15s;}
.tr-dot:nth-child(3){animation-delay:.3s;}
/* confirm card */
.tr-confirm-card{background:var(--s1);border-left:2px solid var(--accent);border-radius:0 10px 10px 0;padding:10px 14px;margin-bottom:var(--sp3);display:flex;align-items:center;gap:var(--sp3);}
.tr-confirm-text{font-family:var(--sans);font-size:13px;color:var(--ink);flex:1;}
.tr-confirm-edit{background:transparent;border:none;font-family:var(--sans);font-size:12px;color:var(--t3);cursor:pointer;padding:0;}
.tr-confirm-log{background:transparent;border:none;font-family:var(--sans);font-size:12px;color:var(--accent);font-weight:500;cursor:pointer;padding:0;}
/* save-as-routine */
.tr-save-card{background:var(--s1);border-radius:10px;padding:10px 14px;margin-bottom:var(--sp3);display:flex;align-items:center;gap:var(--sp3);}
.tr-save-card-text{font-family:var(--sans);font-size:12px;color:var(--t2);flex:1;}
.tr-save-cta{background:transparent;border:none;font-family:var(--sans);font-size:12px;color:var(--accent);font-weight:500;cursor:pointer;padding:0;}
/* quick-repeat */
.tr-quick-repeat{display:flex;align-items:center;gap:var(--sp2);margin-bottom:var(--sp2);}
.tr-qr-label{font-family:var(--sans);font-size:12px;color:var(--t3);}
.tr-qr-btn{font-family:var(--sans);font-size:12px;color:var(--accent);background:transparent;border:none;cursor:pointer;padding:0;}

          <button
            onClick={()=>setLogForm(p=>({...p,type:w.type||'Easy Run',dist:w.dist||w.miles||'',duration:'',exercises:w.exercises||[]}))}
            style={{fontSize:11,padding:'4px 10px',borderRadius:20,border:'0.5px solid var(--b2)',background:'transparent',color:'var(--t2)',cursor:'pointer',fontFamily:'var(--sans)',whiteSpace:'nowrap'}}>
            repeat
          </button>
        </div>
      </div>
    ))}
    {sortedWorkouts.length>8 && (
      <button onClick={()=>setShowAllHistory(p=>!p)}
        style={{width:'100%',marginTop:10,padding:'8px',background:'transparent',border:'none',color:'var(--t3)',fontSize:12,fontFamily:'var(--sans)',cursor:'pointer'}}>
        {showAllHistory ? 'show less' : '+' + (sortedWorkouts.length - 8) + ' more'}
      </button>
    )}
  </div>
)}

/* active routine banner */
.tr-active-routine{display:flex;align-items:center;gap:var(--sp2);margin-bottom:8px;}
.tr-active-routine-name{font-family:var(--sans);font-size:11px;color:var(--t3);letter-spacing:0.04em;}
.tr-clear-routine{background:transparent;border:none;font-family:var(--sans);font-size:11px;color:var(--t4);cursor:pointer;padding:0;}
/* routines section */
.tr-routines-hdr{display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;}
.tr-add-routine{background:transparent;border:none;font-family:var(--sans);font-size:12px;color:var(--accent);cursor:pointer;padding:0;}
.tr-routines-card{background:var(--s1);border:0.5px solid var(--s2);border-radius:var(--r3);overflow:hidden;margin-bottom:var(--sp4);}
/* T-5: hide number spinners on log form */
.tr-view input[type=number]::-webkit-outer-spin-button,
.tr-view input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none;margin:0;}
.tr-view input[type=number]{-moz-appearance:textfield;appearance:textfield;}
/* hide pill scroll scrollbar */
.tr-pill-scroll::-webkit-scrollbar{display:none;}
.tr-pill-scroll{scrollbar-width:none;-ms-overflow-style:none;}
.tr-routine-row{display:flex;align-items:center;gap:var(--sp3);padding:10px 16px;border-bottom:0.5px solid var(--s2);cursor:pointer;transition:background .1s;-webkit-tap-highlight-color:transparent;}
.tr-routine-row:last-child{border-bottom:none;}
.tr-routine-row:hover{background:var(--s2);}
.tr-routine-name{font-family:var(--sans);font-size:14px;color:var(--ink);flex:1;min-width:0;}
.tr-routine-meta{font-family:var(--sans);font-size:12px;color:var(--t3);flex-shrink:0;}
.tr-routine-start{background:transparent;border:none;font-family:var(--sans);font-size:12px;color:var(--accent);cursor:pointer;padding:0;flex-shrink:0;}
.tr-routine-actions{display:flex;gap:var(--sp2);padding:6px 16px 10px;background:var(--s2);}
.tr-routine-edit{font-family:var(--sans);font-size:12px;color:var(--t2);background:transparent;border:none;cursor:pointer;padding:0;}
.tr-routine-delete{font-family:var(--sans);font-size:12px;color:var(--t4);background:transparent;border:none;cursor:pointer;padding:0;}
/* log form */
.tr-form{background:var(--s1);border:0.5px solid var(--s2);border-radius:var(--r3);padding:14px 16px;margin-bottom:var(--sp4);display:flex;flex-direction:column;gap:var(--sp3);}
.tr-field{display:flex;flex-direction:column;gap:4px;}
.tr-field-lbl{font-family:var(--sans);font-size:11px;font-weight:400;color:var(--t3);letter-spacing:0.05em;text-transform:lowercase;margin-bottom:6px;}
.tr-input{height:44px;background:var(--s2);border:0.5px solid var(--b2);border-radius:var(--r2);padding:0 var(--sp3);font-family:var(--sans);font-size:16px;color:var(--ink);outline:none;width:100%;box-sizing:border-box;transition:border-color .1s;}
.tr-input:focus{border-color:var(--accent);}
.tr-input::placeholder{color:var(--t4);}
.tr-select{height:44px;background:var(--s2);border:0.5px solid var(--b2);border-radius:var(--r2);padding:0 var(--sp3);font-family:var(--sans);font-size:16px;color:var(--ink);outline:none;width:100%;box-sizing:border-box;appearance:none;cursor:pointer;}
.tr-textarea{background:var(--s2);border:0.5px solid var(--b2);border-radius:var(--r2);padding:var(--sp2) var(--sp3);font-family:var(--sans);font-size:var(--fs-sm);color:var(--ink);outline:none;width:100%;box-sizing:border-box;resize:vertical;min-height:60px;transition:border-color .1s;}
.tr-textarea:focus{border-color:var(--accent);}
.tr-form-row{display:grid;grid-template-columns:1fr 1fr;gap:var(--sp2);}@media(max-width:480px){.tr-form-row{grid-template-columns:1fr;}}
.tr-submit{height:30px;padding:0 16px;background:var(--accent);border:none;border-radius:var(--r2);color:var(--on-accent);font-family:var(--sans);font-size:var(--fs-xs);font-weight:500;cursor:pointer;transition:opacity .12s;align-self:flex-start;-webkit-tap-highlight-color:transparent;}
.tr-submit:hover{opacity:.88;}
.tr-submit:disabled{background:var(--s3)!important;color:var(--t4)!important;cursor:not-allowed;opacity:1!important;}
/* pill row */
.tr-pill-row{display:flex;gap:4px;flex-wrap:wrap;}
.tr-pill{height:26px;padding:0 10px;border-radius:var(--r2);border:0.5px solid var(--b2);background:var(--s2);font-family:var(--sans);font-size:var(--fs-xs);color:var(--t3);cursor:pointer;transition:all .1s;white-space:nowrap;}
.tr-pill-active{background:var(--accent);border-color:var(--accent);color:var(--on-accent);font-weight:500;}
/* exercise logger */
.tr-exlog{background:var(--s2);border:0.5px solid var(--b1);border-radius:var(--r2);padding:var(--sp3);display:flex;flex-direction:column;gap:var(--sp3);}
.tr-ex-row{background:transparent;border:none;border-bottom:0.5px solid var(--s2);padding:10px 0;display:flex;flex-direction:column;gap:8px;}
.tr-ex-row:last-of-type{border-bottom:none;padding-bottom:0;}
.tr-ex-name-row{display:flex;gap:var(--sp2);align-items:center;}
.tr-ex-hint{font-family:var(--sans);font-size:11px;color:var(--t3);}
.tr-ex-routine-hint{font-family:var(--sans);font-size:10px;color:var(--t4);}
.tr-set-row{display:flex;gap:6px;align-items:center;}
.tr-set-input{height:32px;background:var(--s1);border:0.5px solid var(--s3);border-radius:6px;padding:0 6px;font-family:var(--sans);font-size:13px;color:var(--ink);outline:none;text-align:center;}
.tr-set-input:focus{border-color:var(--accent);}
.tr-set-lbl{font-family:var(--sans);font-size:12px;color:var(--t4);flex-shrink:0;}
.tr-add-set{background:transparent;border:none;font-family:var(--sans);font-size:var(--fs-xs);color:var(--accent);cursor:pointer;padding:2px 0;}
.tr-repeat3{background:transparent;border:0.5px solid var(--s3);border-radius:var(--r);font-family:var(--sans);font-size:var(--fs-xs);color:var(--t3);cursor:pointer;padding:2px 6px;margin-left:4px;}
.tr-rm-btn{background:transparent;border:none;color:var(--t4);cursor:pointer;font-size:14px;padding:0 4px;min-width:24px;}
.tr-rm-btn:hover{color:var(--accent-d);}
.tr-add-ex{background:transparent;border:0.5px solid var(--s3);border-radius:var(--r2);color:var(--t3);font-family:var(--sans);font-size:var(--fs-xs);padding:6px 12px;cursor:pointer;align-self:flex-start;}
.tr-add-ex:hover{border-color:var(--b2);color:var(--t2);}
.tr-drag-handle{color:var(--t4);font-size:14px;cursor:grab;flex-shrink:0;}
/* inline routine builder */
.tr-inline-form{background:var(--s1);border-radius:12px;padding:16px 20px;display:flex;flex-direction:column;gap:16px;margin-top:var(--sp2);overflow:hidden;}
.tr-inline-hdr{display:flex;align-items:center;justify-content:space-between;}
.tr-inline-hdr-label{font-family:var(--sans);font-size:11px;color:var(--t3);letter-spacing:0.05em;text-transform:lowercase;}
.tr-inline-cancel{background:transparent;border:none;font-family:var(--sans);font-size:12px;color:var(--t4);cursor:pointer;padding:0;}
.tr-delete-link{background:transparent;border:none;font-family:var(--sans);font-size:var(--fs-xs);color:var(--t4);cursor:pointer;padding:0;text-align:center;margin-top:4px;}
/* week tiles */
.tr-week-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:var(--sp4);}@media(max-width:380px){.tr-week-grid{grid-template-columns:1fr;}}
.tr-week-tile{background:var(--s2);border-radius:10px;padding:12px 14px;display:flex;flex-direction:column;gap:4px;}
.tr-week-val{font-family:var(--sans);font-size:24px;font-weight:400;color:var(--ink);line-height:1;}
.tr-week-lbl{font-family:var(--sans);font-size:11px;color:var(--t3);}
.tr-week-streak{font-family:var(--sans);font-size:11px;color:var(--accent);}
/* carousel */
.tr-carousel{background:var(--s1);border:0.5px solid var(--s2);border-radius:var(--r3);height:120px;display:flex;flex-direction:column;align-items:center;justify-content:center;cursor:pointer;position:relative;overflow:hidden;-webkit-tap-highlight-color:transparent;}
.tr-carousel-slide{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:3px;animation:tr-fade .3s ease;padding:0 16px;text-align:center;}
@keyframes tr-fade{from{opacity:0}to{opacity:1}}
.tr-carousel-val{font-family:var(--sans);font-size:36px;font-weight:300;color:var(--ink);line-height:1;}
.tr-carousel-ctx{font-family:var(--sans);font-size:13px;color:var(--t2);}
.tr-carousel-accent{font-family:var(--sans);font-size:11px;color:var(--accent);}
.tr-carousel-dots{position:absolute;bottom:8px;display:flex;gap:5px;}
.tr-carousel-dot{width:4px;height:4px;border-radius:50%;background:var(--s3);cursor:pointer;}
.tr-carousel-dot-active{background:var(--accent);}
/* triumphs */
.tr-triumph-row{display:flex;align-items:center;gap:var(--sp3);padding:10px 16px;border-bottom:0.5px solid var(--s2);cursor:pointer;}
.tr-triumph-row:last-child{border-bottom:none;}
.tr-triumph-row:hover{background:var(--s2);}
.tr-triumph-cat{font-family:var(--sans);font-size:13px;color:var(--ink);flex:1;}
.tr-triumph-tag{font-family:var(--sans);font-size:10px;color:var(--t4);}
.tr-triumph-val{font-family:var(--sans);font-size:15px;font-weight:400;color:var(--accent);flex-shrink:0;}
.tr-triumph-date{font-family:var(--sans);font-size:11px;color:var(--t3);flex-shrink:0;}
.tr-triumph-star{color:var(--accent);font-size:10px;flex-shrink:0;}
.tr-triumph-hdr{display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;}
.tr-add-triumph{background:transparent;border:none;font-family:var(--sans);font-size:12px;color:var(--accent);cursor:pointer;padding:0;}
.tr-mi-row{display:flex;align-items:center;gap:var(--sp3);padding:10px 16px;border-bottom:0.5px solid var(--s2);}
.tr-mi-row:last-child{border-bottom:none;}
.tr-mi-label{font-family:var(--sans);font-size:11px;color:var(--t3);letter-spacing:0.04em;width:90px;flex-shrink:0;}
.tr-mi-val{font-family:var(--sans);font-size:15px;font-weight:400;color:var(--ink);flex:1;}
/* coach */
.tr-coach{display:flex;flex-direction:column;gap:var(--sp3);min-height:0;}
.tr-race-banner{font-family:var(--sans);font-size:11px;color:var(--t3);text-align:center;letter-spacing:0.04em;padding:var(--sp2) 0;}
.tr-race-days{color:var(--accent);font-weight:500;}
.tr-coach-empty{display:flex;flex-direction:column;align-items:center;gap:var(--sp4);padding:var(--sp8) 0 var(--sp4);}
.tr-coach-empty-greet{font-family:var(--sans);font-size:var(--fs-base);color:var(--ink);text-align:center;}
.tr-coach-empty-greet span{color:var(--accent);}
.tr-coach-empty-sub{font-family:var(--sans);font-size:var(--fs-sm);color:var(--t3);text-align:center;}
.tr-msgs{display:flex;flex-direction:column;gap:var(--sp3);padding-bottom:var(--sp4);}
.tr-bubble-wrap{display:flex;align-items:flex-start;gap:8px;}
.tr-bubble-wrap-user{flex-direction:row-reverse;}
.tr-bubble-lbl{font-family:var(--sans);font-size:10px;color:var(--t3);letter-spacing:0.04em;margin-bottom:3px;text-transform:lowercase;}
.tr-bubble{border-radius:var(--r3);padding:var(--sp2) var(--sp3);font-family:var(--sans);font-size:var(--fs-sm);color:var(--ink);line-height:1.55;max-width:85%;}
.tr-bubble-coach{background:var(--s1);border:0.5px solid var(--b1);}
.tr-bubble-user{background:var(--s2);border:0.5px solid var(--b2);}
.tr-plan-banner{background:var(--accent-w);border:0.5px solid var(--accent);border-radius:var(--r2);padding:var(--sp2) var(--sp3);margin-top:var(--sp2);font-family:var(--sans);font-size:var(--fs-xs);color:var(--accent);}
.tr-plan-btn{background:var(--accent);border:none;border-radius:var(--r2);padding:5px 12px;font-family:var(--sans);font-size:var(--fs-xs);font-weight:500;color:var(--on-accent);cursor:pointer;margin-top:6px;transition:opacity .12s;display:block;}
.tr-plan-btn:hover{opacity:.88;}
.tr-typing{font-family:var(--sans);font-size:var(--fs-xs);color:var(--t4);padding:var(--sp1) 0;}
.tr-chips{display:flex;gap:6px;overflow-x:auto;scrollbar-width:none;padding-bottom:2px;flex-shrink:0;}
.tr-chips::-webkit-scrollbar{display:none;}
.tr-chip{background:var(--s1);border:0.5px solid var(--s3);border-radius:var(--r2);padding:5px 10px;font-family:var(--sans);font-size:12px;color:var(--t2);cursor:pointer;white-space:nowrap;flex-shrink:0;transition:border-color .1s,color .1s;}
.tr-chip:hover{border-color:var(--b2);color:var(--ink);}
.tr-input-bar{display:flex;gap:var(--sp2);align-items:flex-end;background:var(--s1);border:0.5px solid var(--b2);border-radius:var(--r3);padding:var(--sp2) var(--sp3);transition:border-color .12s;}
.tr-input-bar:focus-within{border-color:var(--accent);box-shadow:0 0 0 2px var(--accent-w);}
.tr-coach-ta{flex:1;background:transparent;border:none;outline:none;font-family:var(--sans);font-size:var(--fs-sm);color:var(--ink);resize:none;min-height:22px;max-height:120px;line-height:1.5;}
.tr-coach-ta::placeholder{color:var(--t4);}
.tr-send-btn{width:30px;height:30px;border-radius:50%;border:none;display:flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;transition:background .1s;background:var(--b2);color:var(--t3);}
.tr-send-btn-on{background:var(--accent)!important;color:var(--on-accent)!important;}
/* stats */
.tr-stats-section{display:flex;flex-direction:column;gap:var(--sp4);}
.tr-card{background:var(--s1);border:0.5px solid var(--s2);border-radius:var(--r3);overflow:hidden;}
.tr-card-left-border{border-left:2px solid var(--accent);}
.tr-card-hdr-lbl{font-family:var(--sans);font-size:11px;color:var(--t3);letter-spacing:0.05em;text-transform:lowercase;padding:12px 16px 8px;border-bottom:0.5px solid var(--s2);}
.tr-race-body{padding:14px 16px;display:flex;flex-direction:column;gap:var(--sp2);}
.tr-race-title{font-family:var(--sans);font-size:13px;color:var(--t2);}
.tr-race-countdown{display:flex;align-items:baseline;gap:6px;}
.tr-race-n{font-family:var(--sans);font-size:36px;font-weight:600;color:var(--accent);line-height:1;}
.tr-race-unit{font-family:var(--sans);font-size:14px;color:var(--t2);}
.tr-race-meta{font-family:var(--sans);font-size:13px;color:var(--ink);}
.tr-race-next{font-family:var(--sans);font-size:13px;color:var(--t3);}
.tr-race-next span{color:var(--t2);font-weight:500;}
.tr-view-plan{font-family:var(--sans);font-size:12px;color:var(--t3);background:transparent;border:none;cursor:pointer;padding:0;margin-top:4px;}
/* mileage overview rows */
.tr-mi-row{display:flex;align-items:center;gap:var(--sp3);padding:12px 16px;border-bottom:0.5px solid var(--s2);}
.tr-mi-row:last-child{border-bottom:none;}
.tr-mi-label{font-family:var(--sans);font-size:11px;color:var(--t3);letter-spacing:0.04em;width:80px;flex-shrink:0;}
.tr-mi-val{font-family:var(--sans);font-size:17px;font-weight:400;color:var(--ink);flex:1;}
/* fun stats grid */
/* workout rows */
.tr-wrow{display:flex;align-items:center;gap:var(--sp3);padding:10px 16px;border-bottom:0.5px solid var(--s2);cursor:pointer;transition:background .1s;position:relative;}
.tr-wrow:last-child{border-bottom:none;}
.tr-wrow:hover{background:var(--s2);}
.tr-wrow:hover .tr-wrow-save{opacity:1;}
.tr-wrow-name{font-family:var(--sans);font-size:14px;font-weight:500;color:var(--ink);flex:1;min-width:0;}
.tr-wrow-sub{font-family:var(--sans);font-size:12px;color:var(--t3);}
.tr-wrow-center{font-family:var(--sans);font-size:13px;color:var(--accent);font-weight:500;flex-shrink:0;}
.tr-wrow-center-dim{color:var(--t2);}
.tr-wrow-nil{color:var(--t4);}
.tr-wrow-date{font-family:var(--sans);font-size:12px;color:var(--t3);flex-shrink:0;}
.tr-wrow-save{font-family:var(--sans);font-size:11px;color:var(--t4);background:transparent;border:none;cursor:pointer;padding:0;opacity:0;transition:opacity .1s;flex-shrink:0;}
.tr-wrow-detail{padding:8px 16px 12px;background:var(--s2);border-bottom:0.5px solid var(--s2);}
.tr-wrow-ex{font-family:var(--sans);font-size:12px;color:var(--t3);margin-bottom:3px;}
.tr-wrow-ex strong{color:var(--t2);font-weight:500;}
/* sparkline rows */
.tr-sparkline-row{display:flex;align-items:center;justify-content:space-between;padding:10px 16px;border-bottom:0.5px solid var(--s2);}
.tr-sparkline-lbl{font-family:var(--sans);font-size:11px;color:var(--t3);letter-spacing:0.04em;}
.tr-sparkline-val{font-family:var(--sans);font-size:11px;color:var(--t2);}
/* overload rows */
.tr-ol-row{display:flex;align-items:center;gap:var(--sp3);padding:10px 16px;border-bottom:0.5px solid var(--s2);}
.tr-ol-row:last-child{border-bottom:none;}
.tr-ol-name{font-family:var(--sans);font-size:14px;color:var(--ink);flex:1;min-width:0;}
.tr-ol-progress{font-family:var(--sans);font-size:13px;color:var(--t3);flex-shrink:0;text-align:right;}
.tr-ol-trend{font-family:var(--sans);font-size:12px;flex-shrink:0;min-width:16px;text-align:center;}
.tr-ol-tag{font-family:var(--sans);font-size:10px;color:var(--t4);flex-shrink:0;}
`;


// ─── Exercise helpers for flat schema (sets/reps/weight as scalars) ──────────
function makeExercise(name='') {
  return { id: `ex${Date.now()}${Math.random().toString(36).slice(2,6)}`, name, sets: 3, reps: '', weight: '' };
}
// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function HealthPage({ workouts=[], goals=[], events=[], habits=[], coachHistory=[], routines=[], triumphs=[], customWorkoutTypes=[], setSt, notify }) {
  const VIEWS=['log','coach','stats','triumphs'];
  const [view, setView]               = useState('log');
  const [coachInput, setCoachInput]   = useState('');
  const [sending, setSending]         = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [insightDismissed, setInsightDismissed] = useState(false);
  const [nlInput, setNlInput]         = useState('');
  const [nlParsing, setNlParsing]     = useState(false);
  const [nlConfirm, setNlConfirm]     = useState(null);
  const [showFallback, setShowFallback] = useState(false);
  const [saveCard, setSaveCard]       = useState(null);
  const [routineSheet, setRoutineSheet] = useState(null);
  const [triumphForm, setTriumphForm] = useState(null);
  const [activeRoutine, setActiveRoutine] = useState(null);
  const [expandedRoutine, setExpandedRoutine] = useState(null);
  const [showNewRoutine, setShowNewRoutine] = useState(false);
  const [newRoutineForm, setNewRoutineForm] = useState({name:'',type:'Strength',exercises:[makeExercise()]});
  const lastType = workouts.length > 0 ? workouts[0].type : 'Easy Run';
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [logForm, setLogForm]         = useState({type:lastType,dist:'',note:'',date:TODAY,exercises:[],duration:'',paceMin:'',paceSec:'',paceUnit:'mi'});
  const [showAddType, setShowAddType]   = useState(false);
  const [newTypeName, setNewTypeName]   = useState('');
  const saveTimer   = useRef(null);
  const msgEndRef   = useRef(null);
  const textareaRef = useRef(null);
  const nlRef       = useRef(null);
  const formRef     = useRef(null);
  const touchStart  = useRef(null);

  const progressiveData = useProgressiveOverload(workouts);
  const insight = useProactiveInsight({ workouts, events, goals, today:TODAY });

  const raceGoal  = useMemo(()=>goals.find(g=>g.targetDate&&(g.type==='race'||/race|marathon|half|5k|10k|triathlon/i.test(g.title||''))),[goals]);
  const raceDate  = raceGoal?.targetDate||null;
  const raceName  = raceGoal?.title||'race';
  const raceDays  = raceDate?daysUntil(raceDate):null;

  const trainStats = useMemo(()=>computeTrainStats(workouts,TODAY),[workouts]);

  // Auto-compute pace from distance + duration
  useEffect(()=>{
    const dist = parseFloat(logForm.dist);
    const dur  = logForm.duration;
    if(!dist || !dur) return;
    const parts = dur.split(':').map(Number);
    let totalSec = 0;
    if(parts.length === 3) totalSec = parts[0]*3600 + parts[1]*60 + parts[2];
    else if(parts.length === 2) totalSec = parts[0]*60 + parts[1];
    else return;
    if(!totalSec) return;
    const paceSec = totalSec / dist;
    const paceMin = Math.floor(paceSec / 60);
    const paceSecRem = Math.round(paceSec % 60);
    setLogForm(p=>({...p,paceMin:String(paceMin),paceSec:String(paceSecRem).padStart(2,'0')}));
  },[logForm.dist, logForm.duration]);

  const weekStats = useMemo(()=>{
    const wa=addDays(TODAY,-7);
    const recent=workouts.filter(w=>w.date>=wa);
    return {count:recent.length,miles:parseFloat(recent.reduce((s,w)=>s+parseFloat(w.dist||0),0).toFixed(1))};
  },[workouts]);

  const thisWeekWorkouts = useMemo(()=>{
    const mon=isoWeekStart(TODAY);
    return workouts.filter(w=>w.date>=mon).sort((a,b)=>b.date.localeCompare(a.date));
  },[workouts]);

  const nextHealthEvent = useMemo(()=>events.filter(e=>e.date>=TODAY&&e.type==='health').sort((a,b)=>a.date.localeCompare(b.date))[0]||null,[events]);
  const upcomingStrength = useMemo(()=>events.find(e=>e.date>=TODAY&&e.date<=addDays(TODAY,1)&&e.type==='health'&&isStrength(e.title)),[events]);

  const knownExercises = useMemo(()=>{
    const s=new Set(COMMON_EXERCISES);
    workouts.forEach(w=>w.exercises?.forEach(e=>s.add(e.name)));
    return [...s].sort();
  },[workouts]);

  const sortedWorkouts = useMemo(()=>[...workouts].sort((a,b)=>b.date.localeCompare(a.date)),[workouts]);

  const repeatCandidate = useMemo(()=>sortedWorkouts.find(w=>{
    const d=Math.round((new Date(TODAY+'T12:00:00')-new Date(w.date+'T12:00:00'))/86400000);
    return d>=1&&d<=2;
  })||null,[sortedWorkouts]);

  // Mileage history for sparklines
  const weekMiData  = useMemo(()=>weeklyMileageHistory(workouts,8),[workouts]);
  const monthMiData = useMemo(()=>monthlyMileageHistory(workouts,8),[workouts]);
  const yearMiData  = useMemo(()=>yearlyMileageHistory(workouts,3),[workouts]);

  const exSparklineData = useCallback((exName)=>
    sortedWorkouts.filter(w=>w.exercises?.some(e=>e.name===exName)).slice(0,8).reverse()
      .map(w=>topWeightForExercise(w,exName)),
  [sortedWorkouts]);

  useEffect(()=>{ if(view==='coach') msgEndRef.current?.scrollIntoView({behavior:'smooth'}); },[coachHistory,view]);

  useEffect(()=>{
    if(saveCard){ if(saveTimer.current)clearTimeout(saveTimer.current); saveTimer.current=setTimeout(()=>setSaveCard(null),8000); }
    return()=>{if(saveTimer.current)clearTimeout(saveTimer.current);};
  },[saveCard]);

  // ─── Swipe ──────────────────────────────────────────────────────────────────
  const handleTouchStart = (e)=>{ touchStart.current=e.touches[0].clientX; };
  const handleTouchEnd   = (e)=>{
    if(touchStart.current===null)return;
    const dx=e.changedTouches[0].clientX-touchStart.current; touchStart.current=null;
    if(Math.abs(dx)<50)return;
    const i=VIEWS.indexOf(view);
    if(dx<0&&i<VIEWS.length-1)setView(VIEWS[i+1]);
    if(dx>0&&i>0)setView(VIEWS[i-1]);
  };

  // ─── Cross-sync: calendar event + habit auto-complete ───────────────────────
  function crossSync(entry) {
    setSt(p => {
      // 1. Calendar event
      const trainEvent = createWorkoutEvent(entry, p.events||[]);
      const newEvt = trainEvent ? [trainEvent] : [];
      // 2. Habit auto-complete
      const updatedHabits = (p.habits||[]).map(h=>{
        const nameMatch = RUN_KW.some(k=>h.name.toLowerCase().includes(k));
        const alreadyDone = (h.completions||[]).includes(entry.date);
        if(nameMatch&&!alreadyDone&&isWorkoutTypeMatch(entry.type,h.name)){
          return{...h,completions:[...(h.completions||[]),entry.date]};
        }
        return h;
      });
      return{...p,events:[...(p.events||[]),...newEvt],habits:updatedHabits};
    });
  }

  function isWorkoutTypeMatch(workoutType,habitName){
    const n=habitName.toLowerCase(),t=workoutType.toLowerCase();
    if(n.includes('run')&&(t.includes('run')||t==='interval'))return true;
    if((n.includes('gym')||n.includes('workout')||n.includes('strength'))&&(t==='gym'||t==='strength'))return true;
    if(n.includes('train')||n.includes('exercise'))return true;
    return false;
  }

  // ─── Log workout ─────────────────────────────────────────────────────────────
  function logWorkout(overrideForm) {
    const form=overrideForm||logForm;
    if(!form.type)return;
    const entry={
      id:`w${Date.now()}`, date:form.date||TODAY,
      type:form.type, dist:parseFloat(form.dist)||0, note:(form.note||'').trim(),
    };
    if(isStrength(form.type)&&form.exercises?.length>0){
      entry.exercises=form.exercises.filter(ex=>ex.name?.trim()).map(ex=>({
        name:ex.name.trim(),
        // Support both flat schema {sets,reps,weight} and array schema {sets:[...]}
        sets:Array.isArray(ex.sets)
          ? ex.sets.filter(s=>s.reps||s.weight).map(s=>({reps:parseInt(s.reps)||0,weight:parseFloat(s.weight)||0}))
          : Array.from({length:parseInt(ex.sets)||1},()=>({reps:parseInt(ex.reps)||0,weight:parseFloat(ex.weight)||0})),
      }));
    }
    setSt(p=>{
      const updated={...p,workouts:[entry,...(p.workouts||[])]};
      if(activeRoutine){
        updated.trainRoutines=(p.trainRoutines||[]).map(r=>r.id===activeRoutine.id?{...r,lastUsed:TODAY,useCount:(r.useCount||0)+1}:r);
      }
      return updated;
    });
    crossSync(entry);
    notify('Workout logged');
    setSaveCard({type:entry.type,dist:entry.dist,exercises:entry.exercises,date:entry.date});
    setLogForm(prev => ({type:prev.type,dist:'',note:'',date:TODAY,exercises:[],duration:'',paceMin:'',paceSec:'',paceUnit:'mi'}));
    setShowAddType(false); setNewTypeName('');
    setActiveRoutine(null); setNlConfirm(null); setShowFallback(false);
  }

  // ─── Routine ops ─────────────────────────────────────────────────────────────
  function loadRoutine(r) {
    setActiveRoutine(r);
    const exercises=r.exercises.map(ex=>{
      const prog=progressiveData[ex.name]; const sugg=prog?.suggestion;
      const w=sugg?String(sugg.weight):String(ex.weight||'');
      return{name:ex.name,sets:Array.from({length:ex.sets||3},()=>({reps:String(sugg?.reps||ex.reps||''),weight:w})),routineWeight:ex.weight};
    });
    setLogForm(p=>({...p,type:r.type,exercises}));
    setSt(p=>({...p,trainRoutines:(p.trainRoutines||[]).map(rt=>rt.id===r.id?{...rt,lastUsed:TODAY,useCount:(rt.useCount||0)+1}:rt)}));
    notify(`${r.name} loaded`);
    setShowFallback(true);
    setTimeout(()=>formRef.current?.scrollIntoView({behavior:'smooth',block:'start'}),100);
  }

  function saveRoutine(routine) {
    setSt(p=>{
      const ex=(p.routines||[]);
      const idx=ex.findIndex(r=>r.id===routine.id);
      return{...p,routines:idx>=0?ex.map(r=>r.id===routine.id?routine:r):[...ex,routine]};
    });
    setRoutineSheet(null);
    notify(routineSheet?.editId?'Routine updated':'Routine saved');
  }
  function deleteRoutine(id){setSt(p=>({...p,trainRoutines:(p.trainRoutines||[]).filter(r=>r.id!==id)}));setRoutineSheet(null);notify('Routine deleted');}

  // Triumph ops
  function saveTriumph(t){
    setSt(p=>{const ex=(p.triumphs||[]);const idx=ex.findIndex(x=>x.id===t.id);return{...p,triumphs:idx>=0?ex.map(x=>x.id===t.id?t:x):[...ex,t]};});
    setTriumphForm(null); notify('Saved');
  }

  function findSimilarRoutine(exercises){
    if(!exercises?.length)return null;
    const names=new Set(exercises.map(e=>e.name));
    return routines.find(r=>r.exercises.length===exercises.length&&r.exercises.every(e=>names.has(e.name)))||null;
  }
  function openSaveAsRoutine(workout){
    const similar=findSimilarRoutine(workout.exercises||[]);
    const initial=similar
      ?{...similar,exercises:workout.exercises?.map(ex=>({name:ex.name,sets:ex.sets?.length||3,reps:ex.sets?.[0]?.reps||8,weight:Math.max(...(ex.sets||[]).map(s=>s.weight||0))}))}
      :{name:workout.type||'New Routine',type:workout.type||'Strength',exercises:(workout.exercises||[]).map(ex=>({name:ex.name,sets:ex.sets?.length||3,reps:ex.sets?.[0]?.reps||8,weight:Math.max(...(ex.sets||[]).map(s=>s.weight||0))}))};
    setRoutineSheet({initial,editId:similar?.id||null});
    setSaveCard(null);
  }

  // ─── NL parse ────────────────────────────────────────────────────────────────
  async function parseNL() {
    const text=nlInput.trim();
    if(!text||nlParsing)return;
    setNlParsing(true); setNlConfirm(null);
    try {
      const prompt=`Parse this workout log entry into structured JSON.\nReturn ONLY valid JSON, no other text.\n\nInput: "${text}"\n\nReturn:\n{\n  "type": "Long Run"|"Easy Run"|"Tempo Run"|"Interval"|"Strength"|"Gym"|"Soccer"|"Cross Train"|"Rest",\n  "dist": number,\n  "duration": number,\n  "note": "string",\n  "exercises": [{ "name": "string", "sets": [{ "reps": number, "weight": number }] }]\n}\n\nExamples:\n"ran 5 miles easy" → {"type":"Easy Run","dist":5,"duration":0,"note":"easy pace","exercises":[]}\n"chest day, bench 3x8x135, ohp 3x8x95" → {"type":"Strength","dist":0,"duration":0,"note":"chest day","exercises":[{"name":"Bench Press","sets":[{"reps":8,"weight":135},{"reps":8,"weight":135},{"reps":8,"weight":135}]},{"name":"Overhead Press","sets":[{"reps":8,"weight":95},{"reps":8,"weight":95},{"reps":8,"weight":95}]}]}\n"rest day" → {"type":"Rest","dist":0,"duration":0,"note":"","exercises":[]}`;
      const res=await fetch('/api/chat',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({model:'claude-sonnet-4-20250514',max_tokens:400,messages:[{role:'user',content:prompt}]})});
      const data=await res.json();
      const raw=data.content?.[0]?.text||'';
      setNlConfirm(JSON.parse(raw.replace(/```json?/g,'').replace(/```/g,'').trim()));
      setNlInput('');
    } catch { setShowFallback(true); }
    setNlParsing(false);
  }

  function confirmLog(){
    if(!nlConfirm)return;
    logWorkout({type:nlConfirm.type||'Easy Run',dist:nlConfirm.dist||'',note:nlConfirm.note||'',date:TODAY,exercises:nlConfirm.exercises||[]});
  }
  function editConfirm(){
    if(!nlConfirm)return;
    setLogForm({type:nlConfirm.type||'Easy Run',dist:String(nlConfirm.dist||''),note:nlConfirm.note||'',date:TODAY,exercises:(nlConfirm.exercises||[]).map(ex=>({name:ex.name,sets:(ex.sets||[]).map(s=>({reps:String(s.reps),weight:String(s.weight)}))}))}); 
    setNlConfirm(null); setShowFallback(true);
    setTimeout(()=>formRef.current?.scrollIntoView({behavior:'smooth',block:'start'}),100);
  }
  function repeatWorkout(){if(!repeatCandidate)return;logWorkout({type:repeatCandidate.type,dist:String(repeatCandidate.dist||''),note:repeatCandidate.note||'',date:TODAY,exercises:repeatCandidate.exercises||[]});notify('Workout repeated');}


  function getLastSession(name) {
    if(!name) return null;
    const prev = sortedWorkouts.find(w=>w.exercises?.some(e=>e.name===name));
    if(!prev) return null;
    const ex = prev.exercises.find(e=>e.name===name);
    if(!ex?.sets?.length) return null;
    return ex.sets.map(s=>`${s.reps}×${s.weight}lb`).join(', ');
  }
  function getSuggestion(name) {
    const sugg = progressiveData[name]?.suggestion;
    if(!sugg) return null;
    return `${sugg.sets}×${sugg.reps}×${sugg.weight}lb`;
  }
  function addExercise() {
    setLogForm(p=>({...p, exercises:[...p.exercises, makeExercise()]}));
  }
  function removeExercise(id) {
    setLogForm(p=>({...p, exercises:p.exercises.filter(e=>e.id!==id)}));
  }
  function updateExercise(id, field, value) {
    setLogForm(p=>({...p, exercises:p.exercises.map(e=>e.id===id?{...e,[field]:value}:e)}));
  }

  function handleInsightCta(ins){
    if(ins.coachMsg){setCoachInput(ins.coachMsg);setView('coach');setTimeout(()=>textareaRef.current?.focus(),100);}
    else if(ins.type==='recovery')logWorkout({type:'Rest',dist:'',note:'rest day',date:TODAY,exercises:[]});
  }

  // ─── Add plan ────────────────────────────────────────────────────────────────
  function addPlanToCalendarAndHealth(plan){
    if(!Array.isArray(plan))return;
    setSt(p=>{
      const stamp=()=>`${Date.now()}${Math.random().toString(36).slice(2)}`;
      const newW=plan.map(item=>({id:`w${stamp()}`,date:item.date,type:item.type,dist:item.dist||0,note:item.note||'',...(item.exercises?{exercises:item.exercises.map(ex=>({name:ex.name,sets:Array.from({length:ex.sets||1},()=>({reps:ex.reps||0,weight:ex.weight||0}))}))}:{})}));
      const newE=plan.map(item=>({id:`e${stamp()}`,date:item.date,title:item.note||item.type,type:'health',category:'training',time:'07:00 AM',source:'coach'}));
      return{...p,workouts:[...(p.workouts||[]),...newW],events:[...(p.events||[]),...newE]};
    });
    notify(`${plan.length} session${plan.length!==1?'s':''} added to calendar`);
  }

  // ─── Coach send ───────────────────────────────────────────────────────────────
  async function sendCoach(){
    const text=coachInput.trim();
    if(!text||sending)return;
    setCoachInput(''); setSending(true);
    setSt(p=>({...p,coachHistory:[...(p.coachHistory||[]),{role:'user',content:text,ts:Date.now()}]}));
    const fitnessGoal=goals.find(g=>g.type!=='race'&&g.targetDate);
    const recentWorkoutsStr=(sortedWorkouts||[]).slice(0,5).map(w=>`${w.date}: ${w.type||'workout'}${(w.dist||w.miles||0)>0?` ${w.dist||w.miles}mi`:''}`).join(', ')||'no recent workouts';
    const strengthHistory=(workouts||[]).filter(w=>(w.exercises||[]).length>0).slice(0,10).map(w=>`${w.date} ${w.type||'strength'}: ${(w.exercises||[]).map(e=>`${e.name||'exercise'}: ${(e.sets||[]).map(s=>`${s.reps||0}×${s.weight||0}lb`).join(', ')}`).join(' | ')}`).join('\n')||'no strength sessions logged';
    const overloadSuggestions=Object.entries(progressiveData||{}).map(([ex,d])=>`${ex}: last ${(d.lastSession?.sets||[]).map(s=>`${s.reps}×${s.weight}lb`).join(', ')||'no data'} → suggest ${d.suggestion?`${d.suggestion.sets}×${d.suggestion.reps}×${d.suggestion.weight}lb`:'no suggestion'}`).join('\n')||'no progression data';
    const savedRoutinesSummary=(routines||[]).map(r=>`${r.name||'routine'} (${r.type||''}): ${(r.exercises||[]).map(e=>`${e.name||''} ${e.sets||0}×${e.reps||0}×${e.weight||0}lb`).join(', ')}`).join('\n')||'no saved routines';
    const raceContext=raceDate?`Upcoming race: ${raceName||'race'} on ${raceDate} (${raceDays||'?'} days away).`:'No race scheduled.';
    const triumphContext=triumphs.length>0?`Personal records:\n${triumphs.filter(t=>t.type==='pr').map(t=>`${t.category} PR: ${t.displayValue} (${t.date})`).join('\n')}\nRaces completed: ${triumphs.filter(t=>t.type==='race').length}\n${triumphs.filter(t=>t.type==='race').slice(0,3).map(t=>`${t.title}: ${t.displayValue} on ${t.date}`).join('\n')}`:'No personal records or races logged yet.';
    const today=new Date().toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric',year:'numeric'});
    const systemPrompt=`You are Mylo Coach — an elite fitness and health coach embedded in the Mylo app. Today is ${today}. You combine sports science precision with the motivational intelligence of a world-class coach. You know this athlete's full history: their PRs, their patterns, their tendencies.

Character: stoic, disciplined, encouraging. Consistency beats intensity. Hard work compounds. Embrace difficulty, trust the process, control what's controllable. Treat missed sessions as data, not failure — adjust and move on.

Style: concise and specific. 1-3 sentences for simple questions. Use point form only for plans or exercise progressions. No markdown, no headers, no asterisks. Always reference actual data — specific weights, distances, dates, PRs. Weave in nutrition naturally when relevant: protein timing, fueling, recovery. Celebrate milestones briefly, then pivot to what's next.
${raceContext}
${fitnessGoal?`Current goal: ${fitnessGoal.title}.`:''}
Recent workouts: ${recentWorkoutsStr}
This week: ${trainStats.weeklyMiles} mi, ${trainStats.weeklyWorkoutCount} sessions
Current streak: ${trainStats.currentStreak} days
Strength history (last 10):\n${strengthHistory}
Progressive overload suggestions:\n${overloadSuggestions}
Saved routines:\n${savedRoutinesSummary}
Rules: reference actual data only; +5lb upper/+10lb lower on all sets complete; hold on failure; +2.5lb after 2 holds; use saved routines and progressive data for specific weight recommendations.
When user requests a plan output JSON inside <<<PLAN>>>...<<<\\/PLAN>>> tags.`;
    try{
      const msgs=[...(coachHistory.slice(-10).map(m=>({role:m.role,content:m.content}))),{role:'user',content:text}];
      const res=await fetch('/api/chat',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({model:'claude-sonnet-4-20250514',max_tokens:1000,stream:false,system:systemPrompt,messages:msgs})});
      const data=await res.json();
      console.log('[coach response]', JSON.stringify(data).slice(0,500));
      const raw=data.content?.find(b=>b.type==='text')?.text||(data.error?.message ? `Error: ${data.error.message}` : "sorry, couldn't respond right now");
      const plan=parsePlan(raw);
      setSt(p=>({...p,coachHistory:[...(p.coachHistory||[]),{role:'assistant',content:stripPlan(raw),ts:Date.now(),plan}]}));
    }catch(e){console.error('[coach error]',e);setSt(p=>({...p,coachHistory:[...(p.coachHistory||[]),{role:'assistant',content:"sorry, couldn't respond right now",ts:Date.now()}]}));}
    setSending(false);
  }

  const CHIPS=['how am I progressing this week?','build me a 4-week running plan','what should I lift today?','am I ready for my race?','adjust this week for a night shift','create a strength progression plan',"what's my marathon pr?",'how many races have I done?'];

  // ─── RENDER ───────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{CSS_TRAIN}</style>
      <div className="tr-page">
        <div className="tr-header">
          <CyclingTitle pageLabel="train" filters={VIEWS} value={view} onChange={setView} alwaysShowSuffix={true}/>
          <button className="tr-log-btn" onClick={()=>setView('log')}>+ log</button>
        </div>

        {/* ══ LOG VIEW ══ */}
        {view==='log'&&(
          <div className="tr-view">

            {/* ── Stats tiles ── */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:12}}>
              <div style={{background:'var(--s1)',borderRadius:12,padding:'14px 16px'}}>
                <div style={{fontSize:26,fontWeight:300,color:'var(--ink)',lineHeight:1}}>{weekStats.miles}</div>
                <div style={{fontSize:10,color:'var(--t3)',marginTop:4,letterSpacing:'0.4px'}}>miles this week</div>
              </div>
              <div style={{background:'var(--s1)',borderRadius:12,padding:'14px 16px'}}>
                <div style={{fontSize:26,fontWeight:300,color:'var(--ink)',lineHeight:1}}>{weekStats.count}</div>
                <div style={{fontSize:10,color:'var(--t3)',marginTop:4,letterSpacing:'0.4px'}}>workouts</div>
              </div>
            </div>

            {insight&&!insightDismissed&&<InsightCard insight={insight} onCta={handleInsightCta} onDismiss={()=>setInsightDismissed(true)}/>}

            {/* ── Log workout card ── */}
            <div style={{background:'var(--s1)',border:'0.5px solid var(--b1)',borderRadius:14,padding:'16px 16px',marginBottom:12}}>

              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
                <span style={{fontFamily:'var(--mono)',fontSize:9,color:'var(--accent)',letterSpacing:'0.1em',textTransform:'uppercase'}}>log workout</span>
              </div>

              {/* Type pill row */}
              <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:16}}>
                {['Easy Run','Long Run','Tempo Run','Intervals','Strength','Gym','Soccer','Cross Train','Rest',...(customWorkoutTypes||[])].map(t=>(
                  <button key={t}
                    onClick={()=>setLogForm(p=>({...p,type:t,exercises:isStrength(t)?p.exercises:[]}))}
                    style={{padding:'6px 14px',borderRadius:20,
                      border:logForm.type===t?'none':'0.5px solid var(--s3)',
                      background:logForm.type===t?'var(--accent)':'transparent',
                      color:logForm.type===t?'var(--on-accent)':'var(--t2)',
                      fontSize:12,fontFamily:'var(--sans)',cursor:'pointer',
                      whiteSpace:'nowrap',flexShrink:0,
                      fontWeight:logForm.type===t?500:400,
                      transition:'background .1s,color .1s,border-color .1s',
                      WebkitTapHighlightColor:'transparent'}}>
                    {t.toLowerCase()}
                  </button>
                ))}
              </div>

              {/* Run fields */}
              {!isStrength(logForm.type)&&logForm.type!=='Rest'&&(
                <>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:12}}>
                    <div style={{display:'flex',flexDirection:'column',gap:5}}>
                      <div style={{fontSize:10,letterSpacing:'0.5px',color:'var(--t3)',fontFamily:'var(--sans)'}}>distance</div>
                      <input style={{height:40,border:'0.5px solid var(--b1)',borderRadius:10,padding:'0 12px',fontSize:14,color:'var(--ink)',background:'var(--bg)',fontFamily:'var(--sans)',outline:'none',width:'100%',boxSizing:'border-box'}}
                        placeholder="0.0 mi" value={logForm.dist||''} onChange={e=>setLogForm(p=>({...p,dist:e.target.value}))}/>
                    </div>
                    <div style={{display:'flex',flexDirection:'column',gap:5}}>
                      <div style={{fontSize:10,letterSpacing:'0.5px',color:'var(--t3)',fontFamily:'var(--sans)'}}>duration</div>
                      <input style={{height:40,border:'0.5px solid var(--b1)',borderRadius:10,padding:'0 12px',fontSize:14,color:'var(--ink)',background:'var(--bg)',fontFamily:'var(--sans)',outline:'none',width:'100%',boxSizing:'border-box'}}
                        placeholder="hh : mm : ss" value={logForm.duration||''} onChange={e=>setLogForm(p=>({...p,duration:e.target.value}))}/>
                    </div>
                  </div>
                  <div style={{marginBottom:14}}>
                    <div style={{fontSize:10,letterSpacing:'0.5px',color:'var(--t3)',fontFamily:'var(--sans)',marginBottom:5}}>pace</div>
                    <div style={{display:'flex',alignItems:'center',gap:10}}>
                      <div style={{display:'flex',alignItems:'center',border:'0.5px solid var(--b1)',borderRadius:10,overflow:'hidden',background:'var(--bg)',height:40}}>
                        <input placeholder="mm" maxLength={2} type="number" min="0" max="59"
                          style={{width:52,height:40,border:'none',background:'transparent',fontSize:16,fontWeight:300,textAlign:'center',color:'var(--ink)',fontFamily:'var(--sans)',outline:'none'}}
                          value={logForm.paceMin||''} onChange={e=>setLogForm(p=>({...p,paceMin:e.target.value}))}/>
                        <span style={{fontSize:18,color:'var(--s3)',padding:'0 2px'}}>:</span>
                        <input placeholder="ss" maxLength={2} type="number" min="0" max="59"
                          style={{width:52,height:40,border:'none',background:'transparent',fontSize:16,fontWeight:300,textAlign:'center',color:'var(--ink)',fontFamily:'var(--sans)',outline:'none'}}
                          value={logForm.paceSec||''} onChange={e=>setLogForm(p=>({...p,paceSec:e.target.value}))}/>
                        <div style={{height:40,padding:'0 12px',display:'flex',alignItems:'center',fontSize:11,color:'var(--t3)',background:'var(--s2)',borderLeft:'0.5px solid var(--b1)',whiteSpace:'nowrap',letterSpacing:'0.3px'}}>
                          min / {logForm.paceUnit||'mi'}
                        </div>
                      </div>
                      <div style={{fontSize:10,color:'var(--t4)',fontFamily:'var(--sans)'}}>auto-fills from dist + duration</div>
                    </div>
                  </div>
                </>
              )}

              {/* Strength fields */}
              {isStrength(logForm.type)&&(
                <>
                  {logForm.exercises.map((ex,ei)=>{
                    const exId = ex.id || `fallback-${ei}`;
                    const lastStr = getLastSession(ex.name);
                    const suggStr = getSuggestion(ex.name);
                    return(
                      <div key={exId} style={{background:'var(--bg)',borderRadius:12,padding:'12px 14px',marginBottom:8}}>
                        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
                          <input
                            value={ex.name}
                            onChange={e=>updateExercise(exId,'name',e.target.value)}
                            placeholder="exercise name"
                            list={`lg-ex-${exId}`}
                            style={{flex:1,height:36,border:'0.5px solid var(--s3)',borderRadius:9,padding:'0 11px',fontSize:13,background:'var(--s1)',color:'var(--ink)',fontFamily:'var(--sans)',outline:'none'}}/>
                          <datalist id={`lg-ex-${exId}`}>{(knownExercises||[]).map(n=><option key={n} value={n}/>)}</datalist>
                          <button onClick={()=>removeExercise(exId)}
                            style={{width:28,height:28,border:'none',background:'rgba(0,0,0,0.05)',borderRadius:7,color:'var(--t3)',cursor:'pointer',fontSize:14,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>×</button>
                        </div>
                        <div style={{display:'flex',alignItems:'center',gap:7,flexWrap:'nowrap'}}>
                          <span style={{fontSize:10,color:'var(--t3)',whiteSpace:'nowrap',fontFamily:'var(--sans)'}}>sets</span>
                          <input type="number" min="1" max="20"
                            value={ex.sets||''}
                            onChange={e=>updateExercise(exId,'sets',parseInt(e.target.value)||'')}
                            style={{width:40,height:36,border:'0.5px solid var(--s3)',borderRadius:9,fontSize:14,textAlign:'center',background:'var(--s1)',color:'var(--ink)',fontFamily:'var(--sans)',outline:'none',WebkitAppearance:'none',MozAppearance:'textfield'}}/>
                          <span style={{fontSize:13,color:'var(--s3)'}}>×</span>
                          <input type="number" min="1"
                            value={ex.reps||''}
                            onChange={e=>updateExercise(exId,'reps',parseInt(e.target.value)||'')}
                            placeholder="8"
                            style={{width:40,height:36,border:'0.5px solid var(--s3)',borderRadius:9,fontSize:14,textAlign:'center',background:'var(--s1)',color:'var(--ink)',fontFamily:'var(--sans)',outline:'none',WebkitAppearance:'none',MozAppearance:'textfield'}}/>
                          <span style={{fontSize:10,color:'var(--t3)',whiteSpace:'nowrap',fontFamily:'var(--sans)'}}>reps</span>
                          <span style={{fontSize:13,color:'var(--s3)'}}>·</span>
                          <input type="number" min="0"
                            value={ex.weight||''}
                            onChange={e=>updateExercise(exId,'weight',parseInt(e.target.value)||'')}
                            placeholder="135"
                            style={{width:52,height:36,border:'0.5px solid var(--s3)',borderRadius:9,fontSize:14,textAlign:'center',background:'var(--s1)',color:'var(--ink)',fontFamily:'var(--sans)',outline:'none',WebkitAppearance:'none',MozAppearance:'textfield'}}/>
                          <span style={{fontSize:10,color:'var(--t3)',fontFamily:'var(--sans)'}}>lb</span>
                        </div>
                        {(lastStr||suggStr)&&(
                          <div style={{fontSize:10,color:'var(--t4)',marginTop:6,lineHeight:1.5,fontFamily:'var(--sans)'}}>
                            {lastStr&&`last: ${lastStr}`}
                            {lastStr&&suggStr&&' · '}
                            {suggStr&&<span style={{color:'var(--accent)'}}>suggest {suggStr}</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <button onClick={addExercise}
                    style={{display:'inline-block',background:'none',border:'none',outline:'none',
                      padding:'0 0 14px',fontFamily:'var(--sans)',fontSize:15,fontWeight:400,
                      color:'var(--accent)',cursor:'pointer',WebkitTapHighlightColor:'transparent'}}>
                    + add exercise
                  </button>
                </>
              )}

              {/* Notes */}
              <div style={{fontSize:10,letterSpacing:'0.5px',color:'var(--t3)',fontFamily:'var(--sans)',marginBottom:5}}>notes</div>
              <textarea
                placeholder="how did it go?"
                value={logForm.note||''}
                onChange={e=>setLogForm(p=>({...p,note:e.target.value}))}
                style={{width:'100%',height:54,border:'0.5px solid var(--b1)',borderRadius:10,
                  padding:'10px 12px',fontSize:13,color:'var(--ink)',background:'var(--bg)',
                  fontFamily:'var(--sans)',resize:'none',outline:'none',lineHeight:1.5,
                  marginBottom:14,boxSizing:'border-box'}}/>

              {/* Log button */}
              <button onClick={()=>logWorkout()}
                style={{display:'block',width:'100%',textAlign:'center',background:'none',
                  border:'none',outline:'none',padding:'10px 0',fontFamily:'var(--sans)',
                  fontSize:15,fontWeight:400,color:'var(--accent)',cursor:'pointer',
                  WebkitTapHighlightColor:'transparent'}}>
                + log workout
              </button>
            </div>

{/* ── Workout history ── */}
{sortedWorkouts.length > 0 && (
  <div style={{background:'var(--s1)',borderRadius:16,padding:'16px 18px',marginBottom:12}}>
    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
      <span style={{fontSize:13,fontWeight:500,color:'var(--t2)',fontFamily:'var(--sans)'}}>recent workouts</span>
      <span style={{fontSize:11,color:'var(--t3)',fontFamily:'var(--mono)'}}>{sortedWorkouts.length} total</span>
    </div>
    {sortedWorkouts.slice(0,showAllHistory?999:8).map((w,i) => (
      <div key={w.id||i} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 0',borderBottom:i<Math.min(7,sortedWorkouts.length-1)?'0.5px solid var(--b1)':'none'}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
            <span style={{fontSize:13,color:'var(--ink)',fontFamily:'var(--sans)',fontWeight:500}}>{w.type||'workout'}</span>
            {(w.dist||w.miles)>0 && <span style={{fontSize:12,color:'var(--t2)',fontFamily:'var(--mono)'}}>{w.dist||w.miles}mi</span>}
            {w.duration && <span style={{fontSize:12,color:'var(--t3)',fontFamily:'var(--mono)'}}>{w.duration}</span>}
          </div>
          <div style={{fontSize:11,color:'var(--t3)',fontFamily:'var(--mono)',marginTop:2}}>{w.date}</div>
        </div>
        <div style={{display:'flex',gap:6,flexShrink:0}}>
          {w.exercises?.length>0 && (
            <button onClick={()=>{const r={id:'r'+Date.now(),name:w.type||'Workout',type:w.type||'Strength',exercises:w.exercises.map(e=>({name:e.name,sets:e.sets||3,reps:e.reps||8,weight:e.weight||0}))};setSt(p=>({...p,routines:[...(p.routines||[]),r]}));if(notify)notify('Saved as routine');}}
              style={{fontSize:11,padding:'4px 10px',borderRadius:20,border:'0.5px solid var(--b2)',background:'transparent',color:'var(--t2)',cursor:'pointer',fontFamily:'var(--sans)',whiteSpace:'nowrap'}}>
              save as routine
            </button>
          )}
          <button onClick={()=>setLogForm(p=>({...p,type:w.type||'Easy Run',dist:w.dist||w.miles||'',duration:'',exercises:w.exercises||[]}))}
            style={{fontSize:11,padding:'4px 10px',borderRadius:20,border:'0.5px solid var(--b2)',background:'transparent',color:'var(--t2)',cursor:'pointer',fontFamily:'var(--sans)',whiteSpace:'nowrap'}}>
            repeat
          </button>
        </div>
      </div>
    ))}
    {sortedWorkouts.length>8 && (
      <button onClick={()=>setShowAllHistory(p=>!p)}
        style={{width:'100%',marginTop:10,padding:'8px',background:'transparent',border:'none',color:'var(--t3)',fontSize:12,fontFamily:'var(--sans)',cursor:'pointer'}}>
        {showAllHistory ? 'show less' : '+' + (sortedWorkouts.length - 8) + ' more'}
      </button>
    )}
  </div>
)}


            {/* ── My routines card ── */}
            <div style={{background:'var(--s1)',borderRadius:16,padding:'16px 18px',marginBottom:12}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:routines.length>0||showNewRoutine?14:0}}>
                <span style={{fontSize:13,fontWeight:500,color:'var(--accent)',fontFamily:'var(--sans)'}}>my routines</span>
                <button onClick={()=>setShowNewRoutine(p=>!p)}
                  style={{background:'none',border:'none',outline:'none',padding:0,
                    fontFamily:'var(--sans)',fontSize:15,fontWeight:400,
                    color:'var(--accent)',cursor:'pointer',WebkitTapHighlightColor:'transparent'}}>
                  {showNewRoutine?'× cancel':'+ routine'}
                </button>
              </div>

              {routines.length===0&&!showNewRoutine&&(
                <div style={{fontSize:12,color:'var(--t3)',fontFamily:'var(--sans)',padding:'4px 0'}}>no saved routines</div>
              )}

              {routines.map(r=>(
                <div key={r.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'11px 0',borderBottom:'0.5px solid var(--s2)'}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:500,color:'var(--ink)',fontFamily:'var(--sans)'}}>{r.name}</div>
                    <div style={{fontSize:12,color:'var(--t3)',marginTop:2,fontFamily:'var(--sans)'}}>{r.exercises.length} exercise{r.exercises.length!==1?'s':''} · {(r.type||'strength').toLowerCase()}</div>
                  </div>
                  <div style={{display:'flex',gap:10,alignItems:'center',flexShrink:0}}>
                    <button onClick={()=>{setSt(p=>({...p,trainRoutines:(p.trainRoutines||[]).filter(x=>x.id!==r.id)}));notify('Routine deleted');}}
                      style={{fontSize:11,color:'var(--t4)',background:'none',border:'none',cursor:'pointer',fontFamily:'var(--sans)'}}>delete</button>
                    <button onClick={()=>loadRoutine(r)}
                      style={{fontSize:13,color:'var(--accent)',background:'none',border:'none',cursor:'pointer',fontFamily:'var(--sans)',flexShrink:0}}>start →</button>
                  </div>
                </div>
              ))}

              {/* New routine inline form */}
              {showNewRoutine&&(
                <div style={{background:'var(--bg)',borderRadius:12,padding:'12px 14px',marginTop:8}}>
                  <input placeholder="routine name e.g. upper body a"
                    value={newRoutineForm.name}
                    onChange={e=>setNewRoutineForm(p=>({...p,name:e.target.value}))}
                    style={{width:'100%',height:40,border:'0.5px solid var(--b1)',borderRadius:10,
                      padding:'0 12px',fontSize:14,color:'var(--ink)',background:'var(--s1)',
                      fontFamily:'var(--sans)',outline:'none',marginBottom:12,boxSizing:'border-box'}}/>
                  {/* Type pills */}
                  <div style={{display:'flex',gap:6,overflowX:'auto',marginBottom:12,paddingBottom:2,scrollbarWidth:'none',touchAction:'pan-x'}}>
                    {['Strength','Gym','Cross Train'].map(t=>(
                      <button key={t} onClick={()=>setNewRoutineForm(p=>({...p,type:t}))}
                        style={{padding:'5px 12px',borderRadius:20,
                          border:newRoutineForm.type===t?'none':'0.5px solid var(--s3)',
                          background:newRoutineForm.type===t?'var(--accent)':'transparent',
                          color:newRoutineForm.type===t?'var(--on-accent)':'var(--t2)',
                          fontSize:11,fontFamily:'var(--sans)',cursor:'pointer',whiteSpace:'nowrap',flexShrink:0}}>
                        {t.toLowerCase()}
                      </button>
                    ))}
                  </div>
                  {/* Exercises */}
                  {newRoutineForm.exercises.map((ex,ei)=>(
                    <div key={ex.id||ei} style={{background:'var(--s1)',borderRadius:10,padding:'10px 12px',marginBottom:8}}>
                      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
                        <input value={ex.name} onChange={e=>setNewRoutineForm(p=>({...p,exercises:p.exercises.map((x,i)=>i===ei?{...x,name:e.target.value}:x)}))}
                          placeholder="exercise name" list={`nr-ex-${ei}`}
                          style={{flex:1,height:34,border:'0.5px solid var(--s3)',borderRadius:8,padding:'0 10px',fontSize:13,background:'var(--bg)',color:'var(--ink)',fontFamily:'var(--sans)',outline:'none'}}/>
                        <datalist id={`nr-ex-${ei}`}>{(knownExercises||[]).map(n=><option key={n} value={n}/>)}</datalist>
                        <button onClick={()=>setNewRoutineForm(p=>({...p,exercises:p.exercises.filter((_,i)=>i!==ei)}))}
                          style={{width:26,height:26,border:'none',background:'rgba(0,0,0,0.05)',borderRadius:6,color:'var(--t3)',cursor:'pointer',fontSize:13,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>×</button>
                      </div>
                      <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'nowrap'}}>
                        <span style={{fontSize:10,color:'var(--t3)',whiteSpace:'nowrap',fontFamily:'var(--sans)'}}>sets</span>
                        <input type="number" min="1" max="20" value={ex.sets||3}
                          onChange={e=>setNewRoutineForm(p=>({...p,exercises:p.exercises.map((x,i)=>i===ei?{...x,sets:parseInt(e.target.value)||1}:x)}))}
                          style={{width:40,height:34,border:'0.5px solid var(--s3)',borderRadius:8,fontSize:13,textAlign:'center',background:'var(--bg)',color:'var(--ink)',fontFamily:'var(--sans)',outline:'none',WebkitAppearance:'none',MozAppearance:'textfield'}}/>
                        <span style={{fontSize:12,color:'var(--s3)'}}>×</span>
                        <input type="number" min="1" value={ex.reps||''} placeholder="8"
                          onChange={e=>setNewRoutineForm(p=>({...p,exercises:p.exercises.map((x,i)=>i===ei?{...x,reps:parseInt(e.target.value)||''}:x)}))}
                          style={{width:40,height:34,border:'0.5px solid var(--s3)',borderRadius:8,fontSize:13,textAlign:'center',background:'var(--bg)',color:'var(--ink)',fontFamily:'var(--sans)',outline:'none',WebkitAppearance:'none',MozAppearance:'textfield'}}/>
                        <span style={{fontSize:10,color:'var(--t3)',fontFamily:'var(--sans)'}}>reps</span>
                        <span style={{fontSize:12,color:'var(--s3)'}}>·</span>
                        <input type="number" min="0" value={ex.weight||''} placeholder="lb"
                          onChange={e=>setNewRoutineForm(p=>({...p,exercises:p.exercises.map((x,i)=>i===ei?{...x,weight:parseInt(e.target.value)||''}:x)}))}
                          style={{width:52,height:34,border:'0.5px solid var(--s3)',borderRadius:8,fontSize:13,textAlign:'center',background:'var(--bg)',color:'var(--ink)',fontFamily:'var(--sans)',outline:'none',WebkitAppearance:'none',MozAppearance:'textfield'}}/>
                        <span style={{fontSize:10,color:'var(--t3)',fontFamily:'var(--sans)'}}>lb</span>
                      </div>
                    </div>
                  ))}
                  <button onClick={()=>setNewRoutineForm(p=>({...p,exercises:[...p.exercises,makeExercise()]}))}
                    style={{display:'inline-block',background:'none',border:'none',outline:'none',
                      padding:'0 0 12px',fontFamily:'var(--sans)',fontSize:14,fontWeight:400,
                      color:'var(--accent)',cursor:'pointer',WebkitTapHighlightColor:'transparent'}}>
                    + add exercise
                  </button>
                  <button
                    onClick={()=>{
                      if(!newRoutineForm.name.trim())return;
                      const routine={id:`r${Date.now()}`,name:newRoutineForm.name.trim(),type:newRoutineForm.type||'Strength',exercises:newRoutineForm.exercises.filter(e=>e.name.trim()).map(e=>({name:e.name.trim(),sets:e.sets||3,reps:e.reps||8,weight:e.weight||0}))};
                      setSt(p=>({...p,trainRoutines:[...(p.trainRoutines||[]),routine]}));
                      setShowNewRoutine(false);
                      setNewRoutineForm({name:'',type:'Strength',exercises:[makeExercise()]});
                      notify('Routine saved');
                    }}
                    style={{display:'block',width:'100%',textAlign:'center',background:'none',
                      border:'none',outline:'none',padding:'10px 0',fontFamily:'var(--sans)',
                      fontSize:15,fontWeight:400,color:'var(--accent)',cursor:'pointer',
                      WebkitTapHighlightColor:'transparent'}}>
                    + save routine
                  </button>
                </div>
              )}
            </div>

          </div>
        )}

        {/* ══ COACH VIEW ══ */}
        {view==='coach'&&(
          <div className="tr-coach">
            {insight&&!insightDismissed&&<InsightCard insight={insight} onCta={handleInsightCta} onDismiss={()=>setInsightDismissed(true)}/>}
            {raceDate&&raceDays!==null&&raceDays>=0&&(
              <div className="tr-race-banner">{raceName.toLowerCase()} · <span className="tr-race-days">{raceDays}</span> day{raceDays!==1?'s':''} out</div>
            )}
            {coachHistory.length===0&&(
              <div className="tr-coach-empty">
                <MyloIcon size={56}/>
                <div className="tr-coach-empty-greet">your <span>training coach</span></div>
                <div className="tr-coach-empty-sub">ask about training, recovery, or what to do next</div>
              </div>
            )}
            {coachHistory.length>0&&(
              <div className="tr-msgs">
                {coachHistory.map((msg,i)=>(
                  <div key={i} className={`tr-bubble-wrap ${msg.role==='user'?'tr-bubble-wrap-user':''}`}>
                    {msg.role==='assistant'?<MyloIcon size={28}/>:<UserAvatar size={28}/>}
                    <div style={{display:'flex',flexDirection:'column',maxWidth:'85%'}}>
                      <div className="tr-bubble-lbl">{msg.role==='assistant'?'coach':'you'}</div>
                      <div className={`tr-bubble ${msg.role==='user'?'tr-bubble-user':'tr-bubble-coach'}`}>
                        {msg.content}
                        {msg.plan&&(
                          <div className="tr-plan-banner"><Calendar size={13} strokeWidth={1.5} style={{display:"inline",verticalAlign:"middle",marginRight:4}}/>{msg.plan.length} session{msg.plan.length!==1?'s':''} planned
                            <button className="tr-plan-btn" onClick={()=>addPlanToCalendarAndHealth(msg.plan)}>add to calendar</button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {sending&&<div className="tr-typing">coach is thinking…</div>}
                <div ref={msgEndRef}/>
              </div>
            )}
            <div className="tr-chips">
              {CHIPS.map((c,i)=><button key={i} className="tr-chip" onClick={()=>{setCoachInput(c);textareaRef.current?.focus();}}>{c}</button>)}
            </div>
            <div className="tr-input-bar">
              <textarea ref={textareaRef} className="tr-coach-ta" rows={1} placeholder="ask your coach…" value={coachInput}
                onChange={e=>{setCoachInput(e.target.value);e.target.style.height='auto';e.target.style.height=Math.min(e.target.scrollHeight,120)+'px';}}
                onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();sendCoach();}}}/>
              <button className={`tr-send-btn ${coachInput.trim()?'tr-send-btn-on':''}`} onClick={sendCoach} disabled={!coachInput.trim()||sending}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 12V2M2 7l5-5 5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            </div>
          </div>
        )}

        {/* ══ STATS VIEW ══ */}
        {view==='stats'&&(
          <div className="tr-stats-section">
            {/* S1 — This week */}
            <div>
              <div className="tr-lbl">this week</div>
              <div style={{background:'transparent',border:'none',marginBottom:'var(--sp3)'}}>
                <div className="tr-week-grid">
                  <div className="tr-week-tile">
                    <div className="tr-week-val">{weekStats.miles}</div>
                    <div className="tr-week-lbl">miles this week</div>
                    <Sparkline data={weekMiData} width={80} height={16}/>
                  </div>
                  <div className="tr-week-tile">
                    <div className="tr-week-val">{weekStats.count}</div>
                    <div className="tr-week-lbl">workouts this week</div>
                    {trainStats.currentStreak>0&&<div className="tr-week-streak">{trainStats.currentStreak} day streak</div>}
                  </div>
                </div>
              </div>
            </div>

            {/* S2 — Mileage overview */}
            <div>
              <div className="tr-lbl">mileage overview</div>
              <div className="tr-card">
                {[{label:'this week',val:trainStats.weeklyMiles,data:weekMiData},{label:'this month',val:trainStats.monthlyMiles,data:monthMiData},{label:'this year',val:trainStats.yearlyMiles,data:yearMiData}].map((row,i)=>(
                  <div key={i} className="tr-mi-row"><div className="tr-mi-label">{row.label}</div><div className="tr-mi-val">{row.val} mi</div><Sparkline data={row.data} width={80} height={14}/></div>
                ))}
              </div>
            </div>

            {/* S3 — Highlights carousel */}
            <div>
              <div className="tr-lbl">highlights</div>
              <HighlightsCarousel workouts={workouts} triumphs={triumphs} stats={trainStats}/>
            </div>

            {/* S4 — Race status */}
            <div>
              <div className="tr-lbl">race status</div>
              <div className="tr-card tr-card-left-border">
                {raceDate&&raceDays!==null&&raceDays>=0?(
                  <div className="tr-race-body">
                    <div className="tr-race-title">{raceName.toLowerCase()} · {fmtDate(raceDate)}</div>
                    <div className="tr-race-countdown"><div className="tr-race-n">{raceDays}</div><div className="tr-race-unit">days out</div></div>
                    <div className="tr-race-meta">this week: {thisWeekWorkouts.length} session{thisWeekWorkouts.length!==1?'s':''} · {weekStats.miles} mi</div>
                    {nextHealthEvent&&<div className="tr-race-next">next: <span>{fmtDate(nextHealthEvent.date)} · {nextHealthEvent.title?.toLowerCase()}</span></div>}
                    <button className="tr-view-plan" onClick={()=>setView('coach')}>view plan →</button>
                  </div>
                ):(
                  <div style={{padding:'14px 16px'}}>
                    <EmptyState message="no race scheduled" cta={{label:'+ add race',onClick:()=>{}}}/>
                  </div>
                )}
              </div>
            </div>


          </div>
        )}

        {/* ══ TRIUMPHS ══ */}
        {view==='triumphs'&&(
          <div className="tr-stats-section">
            {/* PRs */}
            <div>
              <div className="tr-triumph-hdr"><div className="tr-lbl" style={{margin:0}}>personal records</div><button className="tr-add-triumph" onClick={()=>setTriumphForm(triumphForm?.mode==='pr'&&!triumphForm?.initial?.id?null:{mode:'pr',initial:null})}>+ pr</button></div>
              <div className="tr-card">
                {(triumphs||[]).filter(t=>t.type==='pr').length===0?<div style={{padding:'14px 16px'}}><EmptyState message="no personal records yet" cta={{label:'+ add first pr',onClick:()=>setTriumphForm({mode:'pr',initial:null})}}/></div>:
                  (triumphs||[]).filter(t=>t.type==='pr').map(t=>(
                    <div key={t.id} className="tr-triumph-row" onClick={()=>setTriumphForm({mode:'pr',initial:t})}>
                      <div className="tr-triumph-cat">{t.category}</div>
                      <div className="tr-triumph-val">{t.displayValue}</div>
                      <div className="tr-triumph-date">{fmtDate(t.date)}</div>
                    </div>
                  ))}
              </div>
              {triumphForm?.mode==='pr'&&<TriumphInline mode="pr" initial={triumphForm.initial||null} onSave={saveTriumph} onClose={()=>setTriumphForm(null)}/>}
            </div>

            {/* Races */}
            <div>
              <div className="tr-triumph-hdr"><div className="tr-lbl" style={{margin:0}}>races</div><button className="tr-add-triumph" onClick={()=>setTriumphForm(triumphForm?.mode==='race'&&!triumphForm?.initial?.id?null:{mode:'race',initial:null})}>+ race</button></div>
              <div className="tr-card">
                {(triumphs||[]).filter(t=>t.type==='race').length===0?<div style={{padding:'14px 16px'}}><EmptyState message="no races logged" cta={{label:'+ log first race',onClick:()=>setTriumphForm({mode:'race',initial:null})}}/></div>:
                  [...(triumphs||[]).filter(t=>t.type==='race')].sort((a,b)=>b.date.localeCompare(a.date)).map(t=>(
                    <div key={t.id} className="tr-triumph-row" onClick={()=>setTriumphForm({mode:'race',initial:t})}>
                      <div style={{flex:1,minWidth:0}}><div className="tr-triumph-cat">{t.title}</div><div className="tr-triumph-tag">{t.category}</div></div>
                      <div className="tr-triumph-val">{t.displayValue}</div>
                      <div className="tr-triumph-date">{fmtDate(t.date)}</div>
                      {t.pr&&<div className="tr-triumph-star"><Star size={11} strokeWidth={1.5} /></div>}
                    </div>
                  ))}
              </div>
              {triumphForm?.mode==='race'&&<TriumphInline mode="race" initial={triumphForm.initial||null} onSave={saveTriumph} onClose={()=>setTriumphForm(null)}/>}
            </div>

            {/* Milestones */}
            <div>
              <div className="tr-triumph-hdr"><div className="tr-lbl" style={{margin:0}}>milestones</div><button className="tr-add-triumph" onClick={()=>setTriumphForm(triumphForm?.mode==='milestone'&&!triumphForm?.initial?.id?null:{mode:'milestone',initial:null})}>+ milestone</button></div>
              <div className="tr-card">
                {(triumphs||[]).filter(t=>t.type==='milestone').length===0?<div style={{padding:'14px 16px'}}><EmptyState message="no milestones yet — keep going"/></div>:
                  [...(triumphs||[]).filter(t=>t.type==='milestone')].sort((a,b)=>b.date.localeCompare(a.date)).map(t=>(
                    <div key={t.id} className="tr-triumph-row"><div className="tr-triumph-cat">{t.title}</div><div className="tr-triumph-date">{fmtDate(t.date)}</div>{t.pr&&<div className="tr-triumph-star"><Star size={11} strokeWidth={1.5} /></div>}</div>
                  ))}
              </div>
              {triumphForm?.mode==='milestone'&&<TriumphInline mode="milestone" initial={null} onSave={saveTriumph} onClose={()=>setTriumphForm(null)}/>}
            </div>
          </div>
        )}

      </div>
    </>
  );
}
